import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import Select, { components as selectComponents } from 'react-select';
import { useLocation, useSearchParams } from 'react-router-dom';
import './ReportsLayer.css';
import {
  getAccessibleReports,
  getReportDefinition,
  runReport,
  getReportSchedules,
  createReportSchedule,
  updateReportSchedule,
  deleteReportSchedule,
  toggleReportSchedule,
  runReportScheduleNow,
} from '../api/reportingAPI';
import {
  getCustomReports,
  createCustomReport,
  updateCustomReport,
  deleteCustomReport,
} from '../api/customReportingAPI';
import { getEmailGroups } from '../api/email-groupAPI';
import { getOrganisationById } from '../api/organisationAPI';
import { useTableFilters } from '../utils/useTableFilters';
import ReportDataTable from './ReportDataTable';
import { formatLocal } from '../shared/dates';
import { getUserTimeZone } from '../shared/timezone';
import { useAppSettings } from '../utils/AppSettingsContext';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  buildPdfDocument,
  defaultPdfBrandingProvider,
  mapReportRowsForPdf,
  resolvePdfOrientation,
} from '../utils/pdfExport';
import { showToastSuccess, showToastWarning } from '../utils/errorController';

const PAGINATED_TO_DROPDOWN_MAP = {
  'assets': { api: 'assets/for-dropdown', displayField: (a) => `${a.unitNumber || a.assetNumber || ''} ${a.assetDescription || ''}` },
  'task-types': { api: 'task-types', displayField: 'taskTypeName' },
  'task-codes': { api: 'task-codes', displayField: 'name' },
  'modifier-codes': { api: 'modifier-codes', displayField: 'name' },
  'suppliers': { api: 'suppliers/for-dropdown', displayField: 'name' },
  'purchase-order-statuses': { api: 'purchase-order-status', displayField: 'status' },
  'cost-responsibility': { api: 'cost-responsibility', displayField: 'name' },
  'priorities': { api: 'priority', displayField: 'name' },
  'work-order-statuses': { api: 'work-order-status', displayField: 'name' },
  'teams': { api: 'teams', displayField: 'teamName' },
  'event-type': { api: 'event-type', displayField: 'name' },
  'event-status': { api: 'event-status', displayField: 'name' },
  'event-responsibility': { api: 'event-responsibility', displayField: 'name' },
  'event-location': { api: 'event-location', displayField: 'name' },
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStartOfWeek = (date) => {
  const value = new Date(date);
  const day = value.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  value.setDate(value.getDate() + diffToMonday);
  value.setHours(0, 0, 0, 0);
  return value;
};

const getDatePresetRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (preset === 'custom_range') {
    return null;
  }

  if (preset === 'last_24_hours') {
    const from = new Date(today);
    from.setDate(from.getDate() - 1);
    return { from, to: today };
  }

  if (preset === 'last_7_days') {
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    return { from, to: today };
  }

  if (preset === 'last_week') {
    const startOfThisWeek = getStartOfWeek(today);
    const from = new Date(startOfThisWeek);
    from.setDate(from.getDate() - 7);
    const to = new Date(startOfThisWeek);
    to.setDate(to.getDate() - 1);
    return { from, to };
  }

  if (preset === 'last_month') {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from, to };
  }

  return null;
};

const formatFileTimestamp = (date = new Date()) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  const seconds = `${date.getSeconds()}`.padStart(2, '0');
  return `${year}-${month}-${day}_${hours}${minutes}${seconds}`;
};
const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const SEND_NOW_ATTACHMENT_FORMAT_OPTIONS = [
  { value: 'excel', label: 'Excel (.xlsx)' },
  { value: 'pdf', label: 'PDF (.pdf)' },
];
const SCHEDULE_ATTACHMENT_FORMAT_OPTIONS = [
  { value: 'excel', label: 'Excel (.xlsx)' },
  { value: 'pdf', label: 'PDF (.pdf)' },
];

const ORACLE_FREQUENT_TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Africa/Johannesburg',
  'America/Mexico_City',
  'Africa/Monrovia',
  'Asia/Tokyo',
  'America/Jamaica',
  'Europe/Rome',
  'Asia/Hong_Kong',
  'Pacific/Guam',
  'Europe/Athens',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Africa/Cairo',
  'Europe/Copenhagen',
  'Europe/Berlin',
  'Europe/Prague',
  'America/Vancouver',
  'America/Edmonton',
  'America/Toronto',
  'America/Montreal',
  'America/Sao_Paulo',
  'Europe/Brussels',
  'Australia/Perth',
  'Australia/Sydney',
  'Asia/Seoul',
  'Africa/Lagos',
  'Europe/Warsaw',
  'America/Puerto_Rico',
  'Europe/Moscow',
  'Asia/Manila',
  'Atlantic/Reykjavik',
  'Asia/Jerusalem',
];

const TIMEZONE_OFFSET_LABELS = {
  UTC: 'UTC+00',
  'America/New_York': 'UTC-05',
  'America/Chicago': 'UTC-06',
  'America/Denver': 'UTC-07',
  'America/Phoenix': 'UTC-07',
  'America/Los_Angeles': 'UTC-08',
  'America/Anchorage': 'UTC-09',
  'Pacific/Honolulu': 'UTC-10',
  'Africa/Johannesburg': 'UTC+02',
  'America/Mexico_City': 'UTC-06',
  'Africa/Monrovia': 'UTC+00',
  'Asia/Tokyo': 'UTC+09',
  'America/Jamaica': 'UTC-05',
  'Europe/Rome': 'UTC+01',
  'Asia/Hong_Kong': 'UTC+08',
  'Pacific/Guam': 'UTC+10',
  'Europe/Athens': 'UTC+02',
  'Europe/London': 'UTC+00',
  'Europe/Paris': 'UTC+01',
  'Europe/Madrid': 'UTC+01',
  'Africa/Cairo': 'UTC+02',
  'Europe/Copenhagen': 'UTC+01',
  'Europe/Berlin': 'UTC+01',
  'Europe/Prague': 'UTC+01',
  'America/Vancouver': 'UTC-08',
  'America/Edmonton': 'UTC-07',
  'America/Toronto': 'UTC-05',
  'America/Montreal': 'UTC-05',
  'America/Sao_Paulo': 'UTC-03',
  'Europe/Brussels': 'UTC+01',
  'Australia/Perth': 'UTC+08',
  'Australia/Sydney': 'UTC+10',
  'Asia/Seoul': 'UTC+09',
  'Africa/Lagos': 'UTC+01',
  'Europe/Warsaw': 'UTC+01',
  'America/Puerto_Rico': 'UTC-04',
  'Europe/Moscow': 'UTC+04',
  'Asia/Manila': 'UTC+08',
  'Atlantic/Reykjavik': 'UTC+00',
  'Asia/Jerusalem': 'UTC+02',
};

const createInitialScheduleForm = (timezone) => ({
  name: '',
  frequency: 'daily',
  timeOfDay: '08:00',
  dayOfWeek: 1,
  dayOfMonth: 1,
  timezone,
  enabled: true,
  emailGroupIds: [],
  attachmentFormats: ['excel'],
  relativeDateFilters: {
    fromDate: { mode: 'last_days', days: 30 },
    toDate: { mode: 'today' },
  },
});

const DEFAULT_SCHEDULE_TIMEZONE = getUserTimeZone() || 'Australia/Sydney';
const DEFAULT_CREATE_DATE_PRESET = 'last_24_hours';
const PDF_MAX_COLUMNS = 10;
const DATE_PRESET_OPTIONS = [
  { value: 'custom_range', label: 'Custom Range' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_24_hours', label: 'Last 24 Hours' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'last_month', label: 'Last Month' },
];

const DATE_PRESET_LABEL_BY_VALUE = DATE_PRESET_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const FROM_DATE_MODE_OPTIONS = [
  { value: 'last_days', label: 'Last days' },
  { value: 'last_hours', label: 'Last hours' },
  { value: 'last_week_start', label: 'Last week start' },
  { value: 'last_month_start', label: 'Last month start' },
  { value: 'last_year_start', label: 'Last year start' },
  { value: 'today', label: 'Today' },
  { value: 'now', label: 'Now' },
];

const TO_DATE_MODE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'now', label: 'Now' },
  { value: 'last_week_end', label: 'Last week end' },
  { value: 'last_month_end', label: 'Last month end' },
  { value: 'last_year_end', label: 'Last year end' },
];


const getRelativeDateFiltersFromPreset = (presetValue) => {
  if (!presetValue) return {};
  if (presetValue === 'last_7_days') {
    return {
      fromDate: { mode: 'last_days', days: 7 },
      toDate: { mode: 'today' },
    };
  }
  if (presetValue === 'last_24_hours') {
    return {
      fromDate: { mode: 'last_hours', hours: 24 },
      toDate: { mode: 'now' },
    };
  }
  if (presetValue === 'last_week') {
    return {
      fromDate: { mode: 'last_week_start' },
      toDate: { mode: 'last_week_end' },
    };
  }
  if (presetValue === 'last_month') {
    return {
      fromDate: { mode: 'last_month_start' },
      toDate: { mode: 'last_month_end' },
    };
  }
  return {};
};

const estimateReportPageSize = (viewportHeight) => {
  const DEFAULT_PAGE_SIZE = 20;
  if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return DEFAULT_PAGE_SIZE;
  const RESERVED_HEIGHT = 470;
  const APPROX_ROW_HEIGHT = 44;
  const visibleRows = Math.floor((viewportHeight - RESERVED_HEIGHT) / APPROX_ROW_HEIGHT);
  return Math.max(8, Math.min(100, visibleRows));
};

const ReportsLayer = ({ mode = 'run' }) => {
  const isCustomMode = mode === 'custom';
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // State for report selection
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [reportDefinition, setReportDefinition] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [loadingDefinition, setLoadingDefinition] = useState(false);
  const [activePanelTab, setActivePanelTab] = useState('columns');
  const [columnSearch, setColumnSearch] = useState('');
  const [hoveredColumnField, setHoveredColumnField] = useState(null);

  // State for report execution
  const [reportData, setReportData] = useState(null);
  const [reportMetadata, setReportMetadata] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSendNowModal, setShowSendNowModal] = useState(false);
  const [sendNowEmailGroupIds, setSendNowEmailGroupIds] = useState([]);
  const [sendNowAttachmentFormats, setSendNowAttachmentFormats] = useState(
    SEND_NOW_ATTACHMENT_FORMAT_OPTIONS.map((option) => option.value),
  );
  const [sendNowSubmitting, setSendNowSubmitting] = useState(false);
  const [sendNowError, setSendNowError] = useState('');
  const [error, setError] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const runInFlightRef = useRef(false);
  const { appSettings } = useAppSettings();
  const userProfile = useUserProfile();

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    estimateReportPageSize(typeof window !== 'undefined' ? window.innerHeight : 0),
  );
  const [total, setTotal] = useState(0);

  // Section by state
  const [sectionBy, setSectionBy] = useState('none');

  // Sort by state - array of { fieldName, ascending }
  const [sortByFields, setSortByFields] = useState([]);
  const [dragSortIndex, setDragSortIndex] = useState(null);

  // Selected display fields state
  const [selectedFields, setSelectedFields] = useState([]);
  const canViewSchedules = userProfile?.privileges?.includes('report-schedules:view');
  const canCreateSchedules = userProfile?.privileges?.includes('report-schedules:create');
  const canUpdateSchedules = userProfile?.privileges?.includes('report-schedules:update');
  const canDeleteSchedules = userProfile?.privileges?.includes('report-schedules:delete');
  const canRunSchedules = userProfile?.privileges?.includes('report-schedules:run');

  const [reportSchedules, setReportSchedules] = useState([]);
  const [emailGroups, setEmailGroups] = useState([]);
  const [showSchedulesModal, setShowSchedulesModal] = useState(false);
  const [showScheduleFormModal, setShowScheduleFormModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState(
    createInitialScheduleForm(DEFAULT_SCHEDULE_TIMEZONE),
  );
  const [scheduleFormError, setScheduleFormError] = useState('');
  const [selectedDatePreset, setSelectedDatePreset] = useState('');
  const [customReports, setCustomReports] = useState([]);
  const [customReportsSort, setCustomReportsSort] = useState({ field: 'name', direction: 'asc' });
  const [customReportsPage, setCustomReportsPage] = useState(1);
  const [customReportsPageSize, setCustomReportsPageSize] = useState(10);
  const [selectedCustomReportId, setSelectedCustomReportId] = useState(null);
  const [hoveredBaseTableId, setHoveredBaseTableId] = useState(null);
  const [hoveringCustomInfo, setHoveringCustomInfo] = useState(false);
  const [pendingCustomConfiguration, setPendingCustomConfiguration] = useState(null);
  const [savingCustomReport, setSavingCustomReport] = useState(false);
  const [deletingCustomReportId, setDeletingCustomReportId] = useState(null);
  const [customReportToDelete, setCustomReportToDelete] = useState(null);
  const [isEditingCustomReportConfig, setIsEditingCustomReportConfig] = useState(false);
  const [previewBaselineConfig, setPreviewBaselineConfig] = useState(null);
  const [showEditCustomInfoModal, setShowEditCustomInfoModal] = useState(false);
  const [editCustomInfoForm, setEditCustomInfoForm] = useState({
    name: '',
    description: '',
    visibility: 'only_me',
  });
  const [showCreateCustomReportModal, setShowCreateCustomReportModal] = useState(false);
  const [createCustomReportForm, setCreateCustomReportForm] = useState({
    name: '',
    description: '',
    visibility: 'only_me',
  });
  const [pendingCreateBaseTableId, setPendingCreateBaseTableId] = useState(null);
  const lastCustomReportsLocationKeyRef = useRef(null);

  // Build table filters from report definition, converting paginatedDropdown to dropDown
  const tableFilters = useMemo(() => {
    if (!reportDefinition?.filterDefinition?.filters) return [];

    return reportDefinition.filterDefinition.filters.map((filter) => {
      const config = {
        field: filter.field,
        type: filter.type,
        label: filter.label,
        menuBarFilter: 1, // All filters inline
        isMulti: filter.isMulti ?? false,
        required: filter.required,
      };

      // Text filter placeholder
      if (filter.type === 'text') {
        config.placeholder = `Filter by ${filter.label.toLowerCase()}...`;
      }

      // Static options
      if (filter.options) {
        config.options = filter.options;
      }

      // API-based dropdown
      if (filter.api && filter.type === 'dropDown') {
        config.api = filter.api;
        config.displayField = filter.displayField;
      }

      // Convert paginatedDropdown to dropDown using the mapping
      if (filter.type === 'paginatedDropdown' && filter.api) {
        const mapping = PAGINATED_TO_DROPDOWN_MAP[filter.api];
        if (mapping) {
          config.type = 'dropDown';
          config.api = mapping.api;
          config.displayField = mapping.displayField;
        }
      }

      return config;
    });
  }, [reportDefinition]);

  // Section by options from report definition
  const sectionByOptions = useMemo(() => {
    return reportDefinition?.filterDefinition?.sectionByOptions || [];
  }, [reportDefinition]);

  // All available column options for the field selector
  const fieldOptions = useMemo(() => {
    if (!reportDefinition?.filterDefinition?.columns) return [];
    return reportDefinition.filterDefinition.columns.map((col) => ({
      value: col.field,
      label: col.label,
    }));
  }, [reportDefinition]);

  // Columns filtered to only selected fields, preserving the original column order
  const displayColumns = useMemo(() => {
    if (!reportDefinition?.filterDefinition?.columns) return [];
    const selectedFieldValues = new Set(selectedFields.map((f) => f.value));
    return reportDefinition.filterDefinition.columns.filter((col) =>
      selectedFieldValues.has(col.field)
    );
  }, [reportDefinition, selectedFields]);

  // Initialize useTableFilters
  const {
    tempFilters,
    appliedFilterParams,
    setTempFiltersBatch,
    applyFilters,
    resetFilters,
    renderFilterControl,
    renderFilterBadges,
  } = useTableFilters(tableFilters, {});
  const [hasInitializedDateDefaults, setHasInitializedDateDefaults] = useState(false);

  const lastRunSignatureRef = useRef('');

  const reportOptions = useMemo(() => (
    reports.map((report) => ({
      value: report.id,
      label: report.displayName,
      icon: report.reportIcon || 'mdi:file-chart-outline',
    }))
  ), [reports]);

  const timezoneOptions = useMemo(() => {
    const supported =
      typeof Intl !== 'undefined' &&
      typeof Intl.supportedValuesOf === 'function'
        ? new Set(Intl.supportedValuesOf('timeZone'))
        : null;

    return ORACLE_FREQUENT_TIMEZONE_OPTIONS
      .filter((tz) => (supported ? supported.has(tz) : true))
      .map((tz) => ({
        value: tz,
        label: `${tz} (${TIMEZONE_OFFSET_LABELS[tz] || 'UTC'})`,
      }));
  }, []);

  const selectedReportOption = useMemo(
    () => reportOptions.find((option) => option.value === selectedReportId) || null,
    [reportOptions, selectedReportId]
  );
  const selectedCustomReport = useMemo(
    () => customReports.find((item) => Number(item.id) === Number(selectedCustomReportId)) || null,
    [customReports, selectedCustomReportId],
  );
  const isViewingCustomReportMode = isCustomMode && selectedCustomReport && !isEditingCustomReportConfig;

  // Fetch accessible reports on mount
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoadingReports(true);
        const data = await getAccessibleReports();
        setReports(data);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to load reports');
      } finally {
        setLoadingReports(false);
      }
    };
    fetchReports();
  }, []);

  const loadCustomReports = useCallback(async () => {
    if (!isCustomMode) return;
    try {
      const data = await getCustomReports();
      setCustomReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching custom reports:', err);
      setError('Failed to load custom reports');
    }
  }, [isCustomMode]);

  useEffect(() => {
    loadCustomReports();
  }, [loadCustomReports]);

  useEffect(() => {
    let isMounted = true;
    const loadOrganisation = async () => {
      try {
        const org = await getOrganisationById(1);
        if (isMounted) {
          setOrganisation(org || null);
        }
      } catch (err) {
        console.error('Error fetching organisation for PDF export:', err);
      }
    };
    loadOrganisation();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const updateAdaptivePageSize = () => {
      const nextPageSize = estimateReportPageSize(window.innerHeight);
      setPageSize((prev) => {
        if (prev === nextPageSize) return prev;
        setPage(1);
        return nextPageSize;
      });
    };

    updateAdaptivePageSize();
    window.addEventListener('resize', updateAdaptivePageSize);
    return () => window.removeEventListener('resize', updateAdaptivePageSize);
  }, []);

  // Fetch report definition when a report is selected
  useEffect(() => {
    if (!selectedReportId) {
      setReportDefinition(null);
      setReportData(null);
      setReportMetadata(null);
      setHasInitializedDateDefaults(false);
      setSelectedDatePreset('');
      lastRunSignatureRef.current = '';
      return;
    }

    const fetchDefinition = async () => {
      try {
        setLoadingDefinition(true);
        setReportData(null);
        setReportMetadata(null);
        setError(null);
        const data = await getReportDefinition(selectedReportId);
        setReportDefinition(data);
        setHasInitializedDateDefaults(false);
        setSectionBy('none');
        setSortByFields(data?.filterDefinition?.defaultSortByFields || []);
        setPage(1);
        setActivePanelTab('columns');
        setColumnSearch('');

        // Initialize selected fields from defaultDisplayFields
        const defaults = data?.filterDefinition?.defaultDisplayFields;
        const columns = data?.filterDefinition?.columns || [];
        if (defaults && defaults.length > 0) {
          const defaultSet = new Set(defaults);
          setSelectedFields(
            columns
              .filter((col) => defaultSet.has(col.field))
              .map((col) => ({ value: col.field, label: col.label }))
          );
        } else {
          // If no defaults defined, show all columns
          setSelectedFields(columns.map((col) => ({ value: col.field, label: col.label })));
        }

      } catch (err) {
        console.error('Error fetching report definition:', err);
        setError('Failed to load report definition');
      } finally {
        setLoadingDefinition(false);
      }
    };
    fetchDefinition();
  }, [selectedReportId]);

  useEffect(() => {
    if (!isCustomMode || !pendingCustomConfiguration || !reportDefinition || loadingDefinition) return;
    if (Number(pendingCustomConfiguration.reportId || 0) !== Number(selectedReportId || 0)) return;

    const columns = reportDefinition?.filterDefinition?.columns || [];
    const savedSelectedFieldValues = Array.isArray(pendingCustomConfiguration.selectedFields)
      ? pendingCustomConfiguration.selectedFields
      : [];
    if (savedSelectedFieldValues.length > 0) {
      const selectedSet = new Set(savedSelectedFieldValues);
      setSelectedFields(
        columns
          .filter((col) => selectedSet.has(col.field))
          .map((col) => ({ value: col.field, label: col.label })),
      );
    }
    if (pendingCustomConfiguration.filters && typeof pendingCustomConfiguration.filters === 'object') {
      setTempFiltersBatch(pendingCustomConfiguration.filters, { apply: true });
    }
    setPendingCustomConfiguration(null);
  }, [isCustomMode, loadingDefinition, pendingCustomConfiguration, reportDefinition, selectedReportId, setTempFiltersBatch]);

  useEffect(() => {
    if (!reportDefinition?.filterDefinition?.filters?.length) return;

    const filters = reportDefinition.filterDefinition.filters;
    const findDateFilter = (predicate) => filters.find((filter) => {
      if (filter.type !== 'date') return false;
      const label = (filter.label || '').toLowerCase();
      const field = (filter.field || '').toLowerCase();
      return predicate(label, field);
    });

    const fromFilter = findDateFilter(
      (label, field) => (label.includes('from') || field.includes('from')) && (label.includes('date') || field.includes('date'))
    );
    const toFilter = findDateFilter(
      (label, field) => (label.includes('to') || field.includes('to')) && (label.includes('date') || field.includes('date'))
    );

    if (!fromFilter || !toFilter) return;

    const fromValue = tempFilters[fromFilter.field];
    const toValue = tempFilters[toFilter.field];
    const updates = {};
    if (!fromValue) {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 30);
      updates[fromFilter.field] = formatDateInputValue(fromDate);
    }
    if (!toValue) {
      updates[toFilter.field] = formatDateInputValue(new Date());
    }

    if (Object.keys(updates).length > 0) {
      setTempFiltersBatch(updates, { apply: true });
    }
    if (!selectedDatePreset && fromFilter && toFilter) {
      setSelectedDatePreset('custom_range');
    }
  }, [reportDefinition, selectedDatePreset, setTempFiltersBatch, tempFilters]);

  // Validate required filters
  const validateFilters = useCallback(() => {
    if (!reportDefinition?.filterDefinition?.filters) return [];

    const errors = [];
    reportDefinition.filterDefinition.filters.forEach((filter) => {
      if (filter.required) {
        const value = tempFilters[filter.field];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          errors.push(`${filter.label} is required`);
        }
      }
    });
    return errors;
  }, [reportDefinition, tempFilters]);

  const filteredFieldOptions = useMemo(() => {
    if (!columnSearch.trim()) return fieldOptions;
    const query = columnSearch.trim().toLowerCase();
    return fieldOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [columnSearch, fieldOptions]);

  const dateRangeFilterFields = useMemo(() => {
    const filters = reportDefinition?.filterDefinition?.filters || [];
    const findDateFilter = (predicate) => filters.find((filter) => {
      if (filter.type !== 'date') return false;
      const label = (filter.label || '').toLowerCase();
      const field = (filter.field || '').toLowerCase();
      return predicate(label, field);
    });

    const fromFilter = findDateFilter(
      (label, field) => (label.includes('from') || field.includes('from')) && (label.includes('date') || field.includes('date')),
    );
    const toFilter = findDateFilter(
      (label, field) => (label.includes('to') || field.includes('to')) && (label.includes('date') || field.includes('date')),
    );

    if (!fromFilter || !toFilter) return null;
    return {
      fromField: fromFilter.field,
      toField: toFilter.field,
    };
  }, [reportDefinition]);

  const handleDatePresetChange = useCallback((presetValue) => {
    if (!dateRangeFilterFields) return;
    setSelectedDatePreset(presetValue || '');
    if (!presetValue) return;

    const range = getDatePresetRange(presetValue);
    if (!range) return;
    setTempFiltersBatch(
      {
        [dateRangeFilterFields.fromField]: formatDateInputValue(range.from),
        [dateRangeFilterFields.toField]: formatDateInputValue(range.to),
      },
      { apply: true },
    );
  }, [dateRangeFilterFields, setTempFiltersBatch]);

  const handleAddField = useCallback((option) => {
    setSelectedFields((prev) => {
      if (prev.find((field) => field.value === option.value)) {
        return prev;
      }
      return [...prev, option];
    });
  }, []);

  const handleRemoveField = useCallback((fieldValue) => {
    setSelectedFields((prev) => prev.filter((field) => field.value !== fieldValue));
  }, []);

  const handleSortDragStart = useCallback((index) => {
    setDragSortIndex(index);
  }, []);

  const handleSortDrop = useCallback((dropIndex) => {
    if (dragSortIndex === null || dragSortIndex === dropIndex) {
      setDragSortIndex(null);
      return;
    }
    setSortByFields((prev) => {
      if (!Array.isArray(prev) || dragSortIndex < 0 || dragSortIndex >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(dragSortIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    setDragSortIndex(null);
  }, [dragSortIndex]);

  const handleResultsColumnSort = useCallback((fieldName) => {
    if (!isViewingCustomReportMode) return;
    setPage(1);
    setSortByFields((prev) => {
      const existing = Array.isArray(prev)
        ? prev.find((item) => (item?.fieldName || item?.field || '') === fieldName)
        : null;

      if (!existing) {
        return [{ fieldName, ascending: true }];
      }
      if (existing.ascending === false) {
        return [];
      }
      return [{ fieldName, ascending: false }];
    });
  }, [isViewingCustomReportMode]);

  const formatFilterValue = useCallback((value) => {
    if (Array.isArray(value)) {
      if (value.length === 0) return 'None';
      return value.join(', ');
    }
    if (value === null || value === undefined || value === '') return 'None';
    return String(value);
  }, []);

  const resolveColumnLabel = useCallback((fieldName) => {
    const column = reportDefinition?.filterDefinition?.columns?.find(
      (col) => col.field === fieldName,
    );
    return column?.label || fieldName;
  }, [reportDefinition]);

  const currentAppliedFilters = useMemo(() => {
    return Object.entries(tempFilters || {}).filter(([, value]) => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });
  }, [tempFilters]);

  const currentAppliedFiltersForSchedulePreview = useMemo(() => {
    const hiddenDateFields = new Set(['fromDate', 'toDate']);
    if (dateRangeFilterFields?.fromField) {
      hiddenDateFields.add(dateRangeFilterFields.fromField);
    }
    if (dateRangeFilterFields?.toField) {
      hiddenDateFields.add(dateRangeFilterFields.toField);
    }
    return currentAppliedFilters.filter(([field]) => !hiddenDateFields.has(field));
  }, [currentAppliedFilters, dateRangeFilterFields]);

  const shouldHideAbsoluteDateFilters = useMemo(() => {
    if (!dateRangeFilterFields) return false;
    return selectedDatePreset !== 'custom_range';
  }, [dateRangeFilterFields, selectedDatePreset]);

  const resolveFilterLabel = useCallback((fieldName) => {
    const match = tableFilters.find((filter) => filter.field === fieldName);
    return match?.label || fieldName;
  }, [tableFilters]);

  const getSortFieldName = useCallback((sortItem) => {
    return sortItem?.fieldName || sortItem?.field || '';
  }, []);

  const getSortDirectionLabel = useCallback((sortItem) => {
    if (typeof sortItem?.ascending === 'boolean') {
      return sortItem.ascending ? 'ASC' : 'DESC';
    }
    const direction = String(sortItem?.direction || sortItem?.order || '').toUpperCase();
    if (direction === 'DESC') return 'DESC';
    return 'ASC';
  }, []);

  const extractRelativeDateFilters = useCallback((presetValue) => {
    const relativeDateFilters = {};
    const filters = reportDefinition?.filterDefinition?.filters || [];
    const fromDateFilter = filters.find((filter) => filter.field === 'fromDate');
    const toDateFilter = filters.find((filter) => filter.field === 'toDate');
    const presetRelativeFilters = getRelativeDateFiltersFromPreset(presetValue);

    if (fromDateFilter && presetRelativeFilters.fromDate) {
      relativeDateFilters.fromDate = presetRelativeFilters.fromDate;
    } else if (fromDateFilter) {
      relativeDateFilters.fromDate = { mode: 'last_days', days: 30 };
    }
    if (toDateFilter && presetRelativeFilters.toDate) {
      relativeDateFilters.toDate = presetRelativeFilters.toDate;
    } else if (toDateFilter) {
      relativeDateFilters.toDate = { mode: 'today' };
    }
    return relativeDateFilters;
  }, [reportDefinition]);

  const buildSchedulePayload = useCallback(() => {
    const allFilters = Object.fromEntries(
      Object.entries(tempFilters).filter(([, value]) => {
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }),
    );
    const isCreateMode = !editingSchedule;
    const filters = { ...allFilters };
    if (isCreateMode && dateRangeFilterFields) {
      delete filters[dateRangeFilterFields.fromField];
      delete filters[dateRangeFilterFields.toField];
    }

    const hasFromDateFilter = Boolean(
      reportDefinition?.filterDefinition?.filters?.some((filter) => filter.field === 'fromDate'),
    );
    const hasToDateFilter = Boolean(
      reportDefinition?.filterDefinition?.filters?.some((filter) => filter.field === 'toDate'),
    );

    const editRelativeDateFilters = scheduleForm.relativeDateFilters || {};
    const normalizedEditRelativeDateFilters = {};
    if (hasFromDateFilter && editRelativeDateFilters.fromDate) {
      normalizedEditRelativeDateFilters.fromDate = { ...editRelativeDateFilters.fromDate };
    }
    if (hasToDateFilter && editRelativeDateFilters.toDate) {
      normalizedEditRelativeDateFilters.toDate = { ...editRelativeDateFilters.toDate };
    }

    return {
      reportId: editingSchedule?.reportId || selectedReportId,
      name: scheduleForm.name.trim(),
      frequency: scheduleForm.frequency,
      timeOfDay: scheduleForm.timeOfDay,
      dayOfWeek: scheduleForm.frequency === 'weekly' ? Number(scheduleForm.dayOfWeek) : undefined,
      dayOfMonth: scheduleForm.frequency === 'monthly' ? Number(scheduleForm.dayOfMonth) : undefined,
      timezone: scheduleForm.timezone || DEFAULT_SCHEDULE_TIMEZONE,
      enabled: Boolean(scheduleForm.enabled),
      emailGroupIds: scheduleForm.emailGroupIds,
      attachmentFormats: scheduleForm.attachmentFormats?.length
        ? scheduleForm.attachmentFormats
        : ['excel'],
      filters,
      relativeDateFilters: isCreateMode
        ? extractRelativeDateFilters(selectedDatePreset || DEFAULT_CREATE_DATE_PRESET)
        : normalizedEditRelativeDateFilters,
      selectedFields: selectedFields.map((field) => field.value),
      sortBy: sortByFields,
      sectionBy,
    };
  }, [
    dateRangeFilterFields,
    editingSchedule,
    extractRelativeDateFilters,
    reportDefinition,
    scheduleForm,
    selectedDatePreset,
    sectionBy,
    selectedFields,
    selectedReportId,
    sortByFields,
    tempFilters,
  ]);

  const buildSendNowPayload = useCallback((emailGroupIds) => {
    const filters = Object.fromEntries(
      Object.entries(tempFilters).filter(([, value]) => {
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }),
    );

    return {
      reportId: selectedReportId,
      name: `temp-send-now-${selectedReportId}-${Date.now()}`,
      frequency: 'daily',
      timeOfDay: '08:00',
      timezone: DEFAULT_SCHEDULE_TIMEZONE,
      enabled: true,
      emailGroupIds,
      filters,
      relativeDateFilters: extractRelativeDateFilters(),
      selectedFields: selectedFields.map((field) => field.value),
      sortBy: sortByFields,
      sectionBy,
    };
  }, [
    extractRelativeDateFilters,
    sectionBy,
    selectedFields,
    selectedReportId,
    sortByFields,
    tempFilters,
  ]);

  const loadSchedules = useCallback(async () => {
    if (!canViewSchedules) return;
    try {
      const response = await getReportSchedules({ page: 1, pageSize: 100 });
      setReportSchedules(response?.items || []);
    } catch (err) {
      console.error('Error loading report schedules:', err);
      setError('Failed to load scheduled reports');
    }
  }, [canViewSchedules]);

  const filteredReportSchedules = useMemo(() => {
    if (!selectedReportId) {
      return [];
    }
    return reportSchedules.filter(
      (schedule) => Number(schedule.reportId) === Number(selectedReportId),
    );
  }, [reportSchedules, selectedReportId]);

  const loadEmailGroups = useCallback(async () => {
    try {
      const groups = await getEmailGroups();
      setEmailGroups(Array.isArray(groups) ? groups : []);
    } catch (err) {
      console.error('Error loading email groups:', err);
      setScheduleFormError('Unable to load email groups.');
    }
  }, []);

  useEffect(() => {
    loadSchedules();
    loadEmailGroups();
  }, [loadSchedules, loadEmailGroups]);

  const openCreateScheduleModal = useCallback(() => {
    if (!selectedReportId) {
      setScheduleFormError('Select and configure a report before scheduling.');
      return;
    }
    const selectedReportLabel =
      reportOptions.find((option) => option.value === selectedReportId)?.label || 'Report';
    setEditingSchedule(null);
    const browserTimezone = getUserTimeZone() || DEFAULT_SCHEDULE_TIMEZONE;
    setScheduleForm({
      ...createInitialScheduleForm(browserTimezone),
      name: `${selectedReportLabel} Schedule`,
    });
    setSelectedDatePreset(DEFAULT_CREATE_DATE_PRESET);
    setScheduleFormError('');
    setShowScheduleFormModal(true);
  }, [reportOptions, selectedReportId]);

  const openEditScheduleModal = useCallback((schedule) => {
    setEditingSchedule(schedule);
    const columns = reportDefinition?.filterDefinition?.columns || [];
    const scheduleFields = Array.isArray(schedule?.selectedFieldsJson)
      ? schedule.selectedFieldsJson
      : [];
    if (scheduleFields.length > 0) {
      setSelectedFields(
        scheduleFields.map((field) => ({
          value: field,
          label: resolveColumnLabel(field),
        })),
      );
    } else {
      setSelectedFields(columns.map((col) => ({ value: col.field, label: col.label })));
    }
    setScheduleForm({
      name: schedule.name || '',
      frequency: schedule.frequency || 'daily',
      timeOfDay: schedule.timeOfDay || '08:00',
      dayOfWeek: schedule.dayOfWeek ?? 1,
      dayOfMonth: schedule.dayOfMonth ?? 1,
      timezone: schedule.timezone || DEFAULT_SCHEDULE_TIMEZONE,
      enabled: Boolean(schedule.enabled),
      emailGroupIds: (schedule.emailGroupMappings || []).map((mapping) => mapping.emailGroupId),
      attachmentFormats:
        schedule.attachmentFormatsJson && schedule.attachmentFormatsJson.length > 0
          ? schedule.attachmentFormatsJson
          : ['excel'],
      relativeDateFilters: schedule.relativeDateFiltersJson || {
        fromDate: { mode: 'last_days', days: 30 },
        toDate: { mode: 'today' },
      },
    });
    setScheduleFormError('');
    setShowScheduleFormModal(true);
  }, [reportDefinition, resolveColumnLabel]);

  const handleSaveSchedule = useCallback(async () => {
    if (!scheduleForm.name.trim()) {
      setScheduleFormError('Schedule name is required.');
      return;
    }
    if (!scheduleForm.emailGroupIds.length) {
      setScheduleFormError('Select at least one recipient group.');
      return;
    }
    if (!scheduleForm.attachmentFormats?.length) {
      setScheduleFormError('Select at least one attachment format.');
      return;
    }
    if (!editingSchedule && dateRangeFilterFields && !selectedDatePreset) {
      setScheduleFormError('Select a date preset for scheduled date filters.');
      return;
    }

    const payload = buildSchedulePayload();
    try {
      if (editingSchedule) {
        await updateReportSchedule(editingSchedule.id, payload);
        showToastSuccess('Scheduled report updated successfully.');
      } else {
        await createReportSchedule(payload);
        showToastSuccess('Scheduled report created successfully.');
      }
      setShowScheduleFormModal(false);
      setScheduleFormError('');
      await loadSchedules();
    } catch (err) {
      console.error('Error saving report schedule:', err);
      const message = err?.response?.data?.message || 'Failed to save schedule.';
      setScheduleFormError(message);
      showToastWarning(message);
    }
  }, [
    buildSchedulePayload,
    dateRangeFilterFields,
    editingSchedule,
    loadSchedules,
    scheduleForm.attachmentFormats,
    scheduleForm.emailGroupIds.length,
    scheduleForm.name,
    selectedDatePreset,
  ]);

  const handleDeleteSchedule = useCallback(async (scheduleId) => {
    if (!window.confirm('Delete this scheduled report?')) return;
    try {
      await deleteReportSchedule(scheduleId);
      showToastSuccess('Scheduled report deleted successfully.');
      await loadSchedules();
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError('Failed to delete schedule');
      showToastWarning(err?.response?.data?.message || 'Failed to delete schedule.');
    }
  }, [loadSchedules]);

  const handleToggleSchedule = useCallback(async (schedule) => {
    try {
      await toggleReportSchedule(schedule.id, !Boolean(schedule.enabled));
      await loadSchedules();
    } catch (err) {
      console.error('Error toggling schedule:', err);
      setError('Failed to toggle schedule');
    }
  }, [loadSchedules]);

  const handleRunScheduleNow = useCallback(async (scheduleId) => {
    try {
      await runReportScheduleNow(scheduleId);
      showToastSuccess('Report email was triggered just now.');
      setReportSchedules((current) =>
        current.map((schedule) =>
          schedule.id === scheduleId
            ? { ...schedule, status: 'running', enabled: 1 }
            : schedule,
        ),
      );
      await loadSchedules();
    } catch (err) {
      console.error('Error running schedule:', err);
      setError('Failed to run schedule');
    }
  }, [loadSchedules]);

  const openSendNowModal = useCallback(() => {
    if (!selectedReportId || !reportDefinition) {
      setError('Select and run a report before sending via email.');
      return;
    }
    setSendNowEmailGroupIds([]);
    setSendNowAttachmentFormats(SEND_NOW_ATTACHMENT_FORMAT_OPTIONS.map((option) => option.value));
    setSendNowError('');
    setShowSendNowModal(true);
    setShowExportMenu(false);
  }, [reportDefinition, selectedReportId]);

  const handleSendNowEmail = useCallback(async () => {
    if (!selectedReportId || !reportDefinition) {
      setSendNowError('Select a report before sending.');
      return;
    }
    if (!sendNowEmailGroupIds.length) {
      setSendNowError('Select at least one recipient group.');
      return;
    }
    if (!sendNowAttachmentFormats.length) {
      setSendNowError('Select at least one attachment format.');
      return;
    }

    let tempScheduleId = null;
    let sentSuccessfully = false;
    try {
      setSendNowSubmitting(true);
      setSendNowError('');
      const payload = buildSendNowPayload(sendNowEmailGroupIds);
      const created = await createReportSchedule(payload);
      tempScheduleId = created?.id;
      if (!tempScheduleId) {
        throw new Error('Unable to create temporary schedule for send-now.');
      }

      await runReportScheduleNow(tempScheduleId, {
        attachmentFormats: sendNowAttachmentFormats,
      });
      sentSuccessfully = true;
      setShowSendNowModal(false);
    } catch (err) {
      console.error('Error sending report via email:', err);
      setSendNowError(err?.response?.data?.message || err?.message || 'Failed to send report via email.');
    } finally {
      if (tempScheduleId) {
        try {
          await deleteReportSchedule(tempScheduleId);
        } catch (cleanupErr) {
          console.error('Failed to cleanup temporary schedule after send-now:', cleanupErr);
          if (sentSuccessfully) {
            setError('Report email sent, but temporary schedule cleanup failed.');
          }
        }
      }
      setSendNowSubmitting(false);
    }
  }, [
    buildSendNowPayload,
    reportDefinition,
    selectedReportId,
    sendNowAttachmentFormats,
    sendNowEmailGroupIds,
  ]);

  const ReportMenu = (props) => {
    return (
      <selectComponents.Menu {...props}>
        <div className="px-24 pt-16 pb-8 bg-white">
          <div className="fw-semibold text-lg">
            Select a Table
          </div>
        </div>

        {props.children}
      </selectComponents.Menu>
    );
  };

  const ReportMenuList = (props) => (
    <selectComponents.MenuList {...props}>
      <div
        className="d-grid p-16"
        style={{
          gridTemplateColumns: 'repeat(2, minmax(320px, 1fr))',
          gap: '12px',
        }}
      >
        {props.children}
      </div>
    </selectComponents.MenuList>
  );


  const ReportOption = (props) => {
    const { data, isFocused, isSelected } = props;

    return (
      <selectComponents.Option {...props}>
        <div
          className={`border rounded p-3 h-100 d-flex gap-3 ${isFocused || isSelected ? 'bg-light border-primary text-black' : 'bg-white'
            }`}
          style={{ minWidth: 0 }}
        >
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{
              width: 44,
              height: 44,
              backgroundColor: data.bgColor || '#eef2ff',
            }}
          >
            <Icon
              icon={data.icon}
              className="text-primary"
              style={{ fontSize: 20 }}
            />
          </div>

          <div className="d-flex flex-column min-w-0 mt-10">
            <span className="fw-semibold text-md">
              {data.label}
            </span>
          </div>
        </div>
      </selectComponents.Option>
    );
  };


  const ReportSingleValue = (props) => {
    const { data } = props;
    return (
      <selectComponents.SingleValue {...props}>
        <div
          className="d-grid align-items-center"
          style={{ gridTemplateColumns: '36px 1fr', columnGap: '8px' }}
        >
          <Icon icon={data.icon} className="text-primary text-xl" />
          <span>{data.label}</span>
        </div>
      </selectComponents.SingleValue>
    );
  };

  const reportSelectStyles = {
    menu: (base) => ({
      ...base,
      minWidth: 700,
      maxWidth: '90vw',
      overflowX: 'hidden',
      padding: 8,
    }),
    menuList: (base) => ({
      ...base,
      overflowX: 'hidden',
      padding: 0,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: 'transparent',
      padding: 0,
      cursor: 'pointer',
    }),
  };


  // Run the report
  const handleRunReport = useCallback(async () => {
    if (runInFlightRef.current) {
      return;
    }

    const validationErrors = validateFilters();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    try {
      runInFlightRef.current = true;
      setLoadingResults(true);
      setError(null);
      applyFilters();

      // Build params from filters, filtering out empty/null values
      const filterParams = Object.fromEntries(
        Object.entries(appliedFilterParams).filter(([, v]) => {
          if (v === undefined || v === null || v === '') return false;
          if (Array.isArray(v) && v.length === 0) return false;
          return true;
        })
      );

      const params = {
        sectionBy,
        sortBy: sortByFields.length > 0 ? sortByFields : undefined,
        page,
        pageSize,
        timezone: getUserTimeZone(),
        ...filterParams,
      };

      const result = await runReport(selectedReportId, params);
      setReportData(result.data);
      setReportMetadata(result.metadata);
      setTotal(result.total);
    } catch (err) {
      console.error('Error running report:', err);
      setError('Failed to run report');
    } finally {
      runInFlightRef.current = false;
      setLoadingResults(false);
    }
  }, [selectedReportId, appliedFilterParams, sectionBy, sortByFields, page, pageSize, applyFilters, validateFilters]);

  const applyCustomReportConfig = useCallback((savedReport) => {
    if (!savedReport) return;
    const configuration = savedReport.config || savedReport.configurationJson || savedReport.configuration || {};
    const resolvedFromBaseTable = (() => {
      if (!configuration?.baseTable) return null;
      const byId = Number(configuration.baseTable);
      if (Number.isFinite(byId) && byId > 0) return byId;
      const match = reportOptions.find(
        (option) => String(option.label || '').toLowerCase() === String(configuration.baseTable).toLowerCase(),
      );
      return match?.value || null;
    })();
    const nextReportId = Number(configuration.reportId || resolvedFromBaseTable || savedReport.reportId || 0) || null;
    setSelectedCustomReportId(savedReport.id || null);
    if (isCustomMode && savedReport?.id) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('customReportId', String(savedReport.id));
      setSearchParams(nextParams);
    }
    setPendingCustomConfiguration({
      reportId: nextReportId,
      selectedFields: Array.isArray(configuration.selectedFields) ? configuration.selectedFields : [],
      filters: configuration.filters && typeof configuration.filters === 'object' ? configuration.filters : {},
    });
    setSelectedReportId(nextReportId);
    const groupByValue = Array.isArray(configuration.groupBy) && configuration.groupBy.length > 0
      ? configuration.groupBy[0]
      : null;
    setSectionBy(configuration.sectionBy || groupByValue || 'none');
    setSortByFields(Array.isArray(configuration.sortByFields) ? configuration.sortByFields : []);
    setSelectedDatePreset(configuration.selectedDatePreset || '');
    setReportData(null);
    setReportMetadata(null);
    setPage(1);
  }, [isCustomMode, reportOptions, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isCustomMode) return;
    const idFromQuery = Number(searchParams.get('customReportId') || 0);
    if (!idFromQuery || !customReports.length) return;
    if (
      Number(selectedCustomReportId || 0) === idFromQuery &&
      Number(selectedReportId || 0) > 0
    ) {
      return;
    }
    const existing = customReports.find((item) => Number(item.id) === idFromQuery);
    if (existing) {
      applyCustomReportConfig(existing);
    }
  }, [
    applyCustomReportConfig,
    customReports,
    isCustomMode,
    searchParams,
    selectedCustomReportId,
    selectedReportId,
  ]);

  useEffect(() => {
    if (!isCustomMode) return;
    if (lastCustomReportsLocationKeyRef.current === location.key) return;
    lastCustomReportsLocationKeyRef.current = location.key;

    const idFromQuery = Number(searchParams.get('customReportId') || 0);
    if (idFromQuery) return;
    setSelectedCustomReportId(null);
    setSelectedReportId(null);
    setReportDefinition(null);
    setReportData(null);
    setReportMetadata(null);
    setPendingCustomConfiguration(null);
    setIsEditingCustomReportConfig(false);
    lastRunSignatureRef.current = '';
  }, [isCustomMode, location.key, searchParams]);

  const handleSaveCustomReport = useCallback(async (overrides = {}) => {
    if (!selectedReportId || !reportDefinition) {
      showToastWarning('Select a report first.');
      return;
    }

    const effectiveName = (overrides.name || selectedCustomReport?.name || '').trim();
    const effectiveDescription = (overrides.description || selectedCustomReport?.description || '').trim();
    const effectiveVisibility = overrides.visibility || selectedCustomReport?.visibility || 'only_me';

    if (!effectiveName) {
      showToastWarning('Custom report name is required.');
      return;
    }
    if (!Array.isArray(selectedFields) || selectedFields.length === 0) {
      showToastWarning('Include at least one column.');
      return;
    }

    const configuration = {
      baseTable: String(selectedReportId || ''),
      filters: Object.fromEntries(
        Object.entries(tempFilters || {}).filter(([, value]) => {
          if (value === null || value === undefined || value === '') return false;
          if (Array.isArray(value) && value.length === 0) return false;
          return true;
        }),
      ),
      groupBy: sectionBy && sectionBy !== 'none' ? [sectionBy] : [],
      selectedFields: selectedFields.map((field) => field.value),
      sortByFields: Array.isArray(sortByFields) ? sortByFields : [],
      selectedDatePreset: selectedDatePreset || '',
    };

    const payload = {
      name: effectiveName,
      description: effectiveDescription,
      visibility: effectiveVisibility,
      config: configuration,
    };

    try {
      setSavingCustomReport(true);
      const queryCustomReportId = Number(searchParams.get('customReportId') || 0);
      const targetCustomReportId = overrides.forceCreate
        ? 0
        : Number(overrides.forceUpdateId || selectedCustomReportId || queryCustomReportId || 0);

      if (targetCustomReportId > 0) {
        await updateCustomReport(targetCustomReportId, payload);
        showToastSuccess('Custom report updated.');
      } else {
        await createCustomReport(payload);
        showToastSuccess('Custom report saved.');
        if (isCustomMode) {
          setSelectedReportId(null);
          setSelectedCustomReportId(null);
          setReportDefinition(null);
          setReportData(null);
          setReportMetadata(null);
          setPendingCustomConfiguration(null);
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('customReportId');
          setSearchParams(nextParams);
        }
      }
      setIsEditingCustomReportConfig(false);
      setPreviewBaselineConfig(null);
      setCreateCustomReportForm({ name: '', description: '', visibility: 'only_me' });
      setEditCustomInfoForm({ name: '', description: '', visibility: 'only_me' });
      await loadCustomReports();
    } catch (err) {
      console.error('Error saving custom report:', err);
      showToastWarning(err?.response?.data?.message || 'Failed to save custom report.');
    } finally {
      setSavingCustomReport(false);
    }
  }, [
    loadCustomReports,
    isCustomMode,
    reportDefinition,
    searchParams,
    selectedCustomReport?.description,
    selectedCustomReport?.name,
    selectedCustomReport?.visibility,
    selectedCustomReportId,
    sectionBy,
    setSearchParams,
    selectedFields,
    selectedReportId,
    selectedDatePreset,
    sortByFields,
    tempFilters,
  ]);

  const enterPreviewMode = useCallback(() => {
    setPreviewBaselineConfig({
      selectedFields: Array.isArray(selectedFields) ? selectedFields.map((f) => ({ ...f })) : [],
      filters: { ...(tempFilters || {}) },
      sectionBy,
      sortByFields: Array.isArray(sortByFields) ? sortByFields.map((s) => ({ ...s })) : [],
      selectedDatePreset: selectedDatePreset || '',
    });
    setIsEditingCustomReportConfig(true);
  }, [sectionBy, selectedDatePreset, selectedFields, sortByFields, tempFilters]);

  const cancelPreviewChanges = useCallback(() => {
    if (previewBaselineConfig) {
      setSelectedFields(Array.isArray(previewBaselineConfig.selectedFields) ? previewBaselineConfig.selectedFields : []);
      setSectionBy(previewBaselineConfig.sectionBy || 'none');
      setSortByFields(Array.isArray(previewBaselineConfig.sortByFields) ? previewBaselineConfig.sortByFields : []);
      setSelectedDatePreset(previewBaselineConfig.selectedDatePreset || '');
      setTempFiltersBatch(previewBaselineConfig.filters || {}, { apply: true });
    } else if (selectedCustomReport) {
      applyCustomReportConfig(selectedCustomReport);
    }
    setIsEditingCustomReportConfig(false);
    setPreviewBaselineConfig(null);
  }, [applyCustomReportConfig, previewBaselineConfig, selectedCustomReport, setTempFiltersBatch]);

  const openEditCustomInfoModal = useCallback(() => {
    if (!selectedCustomReport) return;
    setEditCustomInfoForm({
      name: selectedCustomReport.name || '',
      description: selectedCustomReport.description || '',
      visibility: selectedCustomReport.visibility || 'only_me',
    });
    setShowEditCustomInfoModal(true);
  }, [selectedCustomReport]);

  const handleDeleteCustomReport = useCallback(async (item = customReportToDelete) => {
    if (!item?.id) return;
    try {
      setDeletingCustomReportId(item.id);
      await deleteCustomReport(item.id);
      showToastSuccess('Custom report deleted.');
      if (Number(selectedCustomReportId) === Number(item.id)) {
        setSelectedCustomReportId(null);
      }
      await loadCustomReports();
    } catch (err) {
      console.error('Error deleting custom report:', err);
      showToastWarning(err?.response?.data?.message || 'Failed to delete custom report.');
    } finally {
      setDeletingCustomReportId(null);
      setCustomReportToDelete(null);
    }
  }, [customReportToDelete, loadCustomReports, selectedCustomReportId]);

  const resolveCustomReportCategory = useCallback((item) => {
    const configuration = item?.config || item?.configurationJson || item?.configuration || {};
    const rawBaseTable = configuration?.baseTable;
    if (!rawBaseTable) return '-';
    const asNumber = Number(rawBaseTable);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      const match = reportOptions.find((option) => Number(option.value) === asNumber);
      return match?.label || String(rawBaseTable);
    }
    return String(rawBaseTable);
  }, [reportOptions]);

  const isCustomReportAuthor = useCallback((item) => {
    if (!item) return false;
    return Number(item.createdBy || 0) === Number(userProfile?.id || 0);
  }, [userProfile?.id]);

  const sortedCustomReports = useMemo(() => {
    const rows = Array.isArray(customReports) ? [...customReports] : [];
    const { field, direction } = customReportsSort;
    const factor = direction === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      const aCategory = resolveCustomReportCategory(a).toLowerCase();
      const bCategory = resolveCustomReportCategory(b).toLowerCase();
      const aName = String(a?.name || '').toLowerCase();
      const bName = String(b?.name || '').toLowerCase();
      const aVisibility = String(a?.visibility || '').toLowerCase();
      const bVisibility = String(b?.visibility || '').toLowerCase();

      let cmp = 0;
      if (field === 'category') cmp = aCategory.localeCompare(bCategory);
      else if (field === 'visibility') cmp = aVisibility.localeCompare(bVisibility);
      else cmp = aName.localeCompare(bName);

      if (cmp === 0) cmp = aName.localeCompare(bName);
      return cmp * factor;
    });
    return rows;
  }, [customReports, customReportsSort, resolveCustomReportCategory]);

  const totalCustomReportsPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedCustomReports.length / customReportsPageSize));
  }, [customReportsPageSize, sortedCustomReports.length]);

  const pagedCustomReports = useMemo(() => {
    const start = (customReportsPage - 1) * customReportsPageSize;
    return sortedCustomReports.slice(start, start + customReportsPageSize);
  }, [customReportsPage, customReportsPageSize, sortedCustomReports]);

  const customReportsStartRecord = useMemo(() => {
    if (sortedCustomReports.length === 0) return 0;
    return (customReportsPage - 1) * customReportsPageSize + 1;
  }, [customReportsPage, customReportsPageSize, sortedCustomReports.length]);

  const customReportsEndRecord = useMemo(() => {
    if (sortedCustomReports.length === 0) return 0;
    return Math.min(customReportsPage * customReportsPageSize, sortedCustomReports.length);
  }, [customReportsPage, customReportsPageSize, sortedCustomReports.length]);

  useEffect(() => {
    if (customReportsPage > totalCustomReportsPages) {
      setCustomReportsPage(totalCustomReportsPages);
    }
  }, [customReportsPage, totalCustomReportsPages]);

  const toggleCustomReportsSort = useCallback((field) => {
    setCustomReportsPage(1);
    setCustomReportsSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'asc' };
    });
  }, []);

  const sortIconFor = useCallback((field) => {
    if (customReportsSort.field !== field) return 'mdi:swap-vertical';
    return customReportsSort.direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down';
  }, [customReportsSort.direction, customReportsSort.field]);

  useEffect(() => {
    if (!selectedReportId || !reportDefinition || loadingDefinition) return;
    const signature = JSON.stringify({ selectedReportId, sectionBy, sortByFields, tempFilters });
    if (signature === lastRunSignatureRef.current) return;
    lastRunSignatureRef.current = signature;
    handleRunReport();
  }, [selectedReportId, reportDefinition, loadingDefinition, sectionBy, sortByFields, tempFilters, handleRunReport]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  // Re-run report when page changes (after initial run)
  useEffect(() => {
    if (reportData) {
      handleRunReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Export to Excel (all pages)
  const handleExportExcel = useCallback(() => {
    if (!reportData || !reportDefinition) return;
    setShowExportConfirm(true);
  }, [reportData, reportDefinition]);
  
  const doExportExcel = useCallback(async () => {
    setShowExportConfirm(false);
    
    try {
      setExportingExcel(true);

      const filterParams = Object.fromEntries(
        Object.entries(appliedFilterParams).filter(([, v]) => {
          if (v === undefined || v === null || v === '') return false;
          if (Array.isArray(v) && v.length === 0) return false;
          return true;
        })
      );

      const result = await runReport(selectedReportId, {
        sectionBy,
        sortBy: sortByFields.length > 0 ? sortByFields : undefined,
        page: 1,
        pageSize: total,
        timezone: getUserTimeZone(),
        ...filterParams,
      });

      const columns = displayColumns;
      const headers = columns.map((col) => col.label);
      const tz = getUserTimeZone();

      const rows = result.data.map((row) =>
        columns.map((col) => {
          const value = row[col.field];
          if (col.format === 'boolean') {
            return value ? 'Yes' : 'No';
          }
          if (col.format === 'date' && value) {
            return formatLocal(value, tz, 'yyyy-MM-dd HH:mm');
          }
          return value ?? '';
        })
      );

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportDefinition.displayName || 'report'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
      setError('Failed to export report');
    } finally {
      setExportingExcel(false);
    }
  }, [displayColumns, total, selectedReportId, appliedFilterParams, sectionBy, sortByFields, reportDefinition]);

  const buildFilterSummary = useCallback(() => {
    if (!reportDefinition?.filterDefinition?.filters) return '';
    const labels = reportDefinition.filterDefinition.filters
      .map((filter) => {
        const value = appliedFilterParams?.[filter.field];
        if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          return null;
        }
        const renderedValue = Array.isArray(value) ? value.join(', ') : value;
        return `${filter.label}: ${renderedValue}`;
      })
      .filter(Boolean);
    return labels.join(' | ');
  }, [appliedFilterParams, reportDefinition]);

  const handleExportPdf = useCallback(async (configOverride = {}) => {
    if (!reportDefinition || !selectedReportId) return;
    try {
      setExportingPdf(true);
      const filterParams = Object.fromEntries(
        Object.entries(appliedFilterParams).filter(([, v]) => {
          if (v === undefined || v === null || v === '') return false;
          if (Array.isArray(v) && v.length === 0) return false;
          return true;
        })
      );
      const result = await runReport(selectedReportId, {
        sectionBy,
        sortBy: sortByFields.length > 0 ? sortByFields : undefined,
        page: 1,
        pageSize: total,
        timezone: getUserTimeZone(),
        ...filterParams,
      });

      const pdfColumns = displayColumns.slice(0, PDF_MAX_COLUMNS);
      if (displayColumns.length > PDF_MAX_COLUMNS) {
        showToastWarning(
          `PDF export is limited to ${PDF_MAX_COLUMNS} columns. Extra columns were trimmed. Export to Excel to include all columns.`,
        );
      }

      const orientation = configOverride?.orientation || resolvePdfOrientation(pdfColumns.length);
      const timeZone = getUserTimeZone();
      const rows = mapReportRowsForPdf(result?.data || [], pdfColumns, timeZone);
      const branding = defaultPdfBrandingProvider(appSettings, userProfile, {
        logo: organisation?.orgLogo || organisation?.org_logo || configOverride?.branding?.logo,
        companyName: organisation?.orgName || organisation?.org_name || configOverride?.branding?.companyName,
        ...configOverride?.branding,
      });
      const title = reportDefinition.displayName || 'Report';
      const timestampSuffix = formatFileTimestamp();
      const generatedAtLabel = `Generated: ${formatLocal(new Date().toISOString(), timeZone, 'yyyy-MM-dd HH:mm')} (${timeZone})`;
      const filtersSummary = buildFilterSummary();
      const orgInfo = [
        organisation?.orgName || organisation?.org_name,
        organisation?.orgAddress || organisation?.org_address,
        organisation?.orgCity || organisation?.org_city,
        organisation?.orgState || organisation?.org_state,
        organisation?.orgZip || organisation?.org_zip,
      ].filter(Boolean).join(', ');
      const subtitle = orgInfo || 'Organisation';
      const headerFiltersLabel = filtersSummary ? `Filters: ${filtersSummary}` : 'No filters applied';

      const { blob, filename } = await buildPdfDocument(
        {
          orientation,
          filename: `${title}_${timestampSuffix}.pdf`,
          branding,
          metadata: {
            title,
            subtitle,
            generatedAtLabel: `${generatedAtLabel} | ${headerFiltersLabel}`,
          },
          ...configOverride,
        },
        {
          columns: pdfColumns.map((column) => ({ label: column.label, field: column.field })),
          rows,
        }
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PDF report:', err);
      setError('Failed to export report to PDF');
      throw err;
    } finally {
      setExportingPdf(false);
    }
  }, [
    reportDefinition,
    selectedReportId,
    appliedFilterParams,
    sectionBy,
    sortByFields,
    total,
    displayColumns,
    appSettings,
    userProfile,
    buildFilterSummary,
  ]);

  // Loading state
  if (loadingReports) {
    return (
      <div className="card h-100 p-0 rounded-0">
        <div className="card-body d-flex align-items-center justify-content-center">
          <span className="spinner-border spinner-border-sm me-2" />
          Loading reports...
        </div>
      </div>
    );
  }

  // No reports available
  if (reports.length === 0) {
    return (
      <div className="card h-100 p-0 rounded-0">
        <div className="card-body d-flex align-items-center justify-content-center">
          <p className="text-muted">No reports available. Contact your administrator to grant access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header border-0 pb-24">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-16">
          <div>
            <div className="text-muted text-sm">
              {isCustomMode
                ? 'Create, save, and reuse report configurations.'
                : 'Run reports on demand and schedule recurring report delivery.'}
            </div>
          </div>
          <div className={`badge ${isCustomMode ? 'bg-success-focus text-success-main' : 'bg-primary-focus text-primary-main'} px-12 py-8`}>
            Mode: {isCustomMode ? 'Saved Report Builder' : 'Run & Schedule'}
          </div>
        </div>
        {isCustomMode && selectedCustomReport && (
          <div
            className="mb-12 bg-white"
            onMouseEnter={() => setHoveringCustomInfo(true)}
            onMouseLeave={() => setHoveringCustomInfo(false)}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="fw-semibold">{selectedCustomReport.name}</div>
              {hoveringCustomInfo && (
                <button
                  type="button"
                  className="btn btn-link p-0 text-primary text-sm"
                  onClick={openEditCustomInfoModal}
                >
                  Edit info
                </button>
              )}
            </div>
            <div className="text-sm text-muted">{selectedCustomReport.description || 'No description provided.'}</div>
          </div>
        )}

        <div className={`d-flex align-items-end gap-3 ${isCustomMode ? 'flex-wrap' : 'flex-nowrap'}`}>
          {/* Report Selection */}
          {!isCustomMode && (
            <div className="d-flex flex-column reports-select-wrap">
              <label className="form-label text-sm mb-1">{isCustomMode ? 'Base Table' : 'Select Report'}</label>
              <Select
                classNamePrefix="react-select"
                value={selectedReportOption}
                options={reportOptions}
                onChange={(option) => setSelectedReportId(option ? option.value : null)}
                isClearable
                placeholder="-- Select a base table to get started --"
                components={{
                  Menu: ReportMenu,
                  MenuList: ReportMenuList,
                  Option: ReportOption,
                  SingleValue: ReportSingleValue,
                }}
                styles={reportSelectStyles}
              />
            </div>
          )}
          {isCustomMode && selectedReportId && (
            <div className="d-flex flex-column justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                onClick={() => {
                  setSelectedReportId(null);
                  setSelectedCustomReportId(null);
                  setReportDefinition(null);
                  setReportData(null);
                  setReportMetadata(null);
                  setPendingCustomConfiguration(null);
                  setIsEditingCustomReportConfig(false);
                  setPreviewBaselineConfig(null);
                  setCreateCustomReportForm({ name: '', description: '', visibility: 'only_me' });
                  setPendingCreateBaseTableId(null);
                  lastRunSignatureRef.current = '';
                  if (isCustomMode) {
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.delete('customReportId');
                    setSearchParams(nextParams);
                  }
                }}
              >
                <Icon icon="mdi:arrow-left" className="fs-6" />
                Go back to My Custom Reports
              </button>
            </div>
          )}

          {/* Filters (when report is selected) */}
          {reportDefinition && !loadingDefinition && (
            <>

              {/* Run Report Button */}
              {/*<div className="d-flex flex-column justify-content-end">
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={handleRunReport}
                  disabled={loadingResults}
                >
                  {loadingResults ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <Icon icon="mdi:play" className="icon" />
                  )}
                  Run Report
                </button>
              </div>*/}

              {/* Reset Button */}
              {canViewSchedules && !isCustomMode && (
                <div className="d-flex flex-column justify-content-end">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      setShowSchedulesModal(true);
                      loadSchedules();
                    }}
                  >
                    Scheduled Reports
                  </button>
                </div>
              )}

              {canCreateSchedules && !isCustomMode && (
                <div className="d-flex flex-column justify-content-end">
                  <button
                    className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                    onClick={openCreateScheduleModal}
                    disabled={!selectedReportId}
                  >
                    <Icon icon="mdi:plus" className="fs-5" />
                    Create a Scheduled Report
                  </button>
                </div>
              )}
              {isCustomMode && (
                <div className="d-flex flex-column justify-content-end">
                  {selectedCustomReport ? (
                    <div className="d-flex align-items-center gap-2">
                      {!isEditingCustomReportConfig && (
                        <button
                          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                          onClick={enterPreviewMode}
                          disabled={!selectedReportId || savingCustomReport}
                        >
                          <Icon icon="mdi:eye-edit-outline" className="fs-5" />
                          Edit Configuration
                        </button>
                      )}
                      {isEditingCustomReportConfig && (
                        <>
                          <button
                            className="btn btn-success btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                      onClick={() => handleSaveCustomReport()}
                      disabled={!selectedReportId || savingCustomReport}
                    >
                            <Icon icon="mdi:content-save-outline" className="fs-5" />
                            {savingCustomReport ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                            onClick={cancelPreviewChanges}
                            disabled={savingCustomReport}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <button
                      className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                      onClick={async () => {
                        const name = (createCustomReportForm.name || '').trim();
                        if (!name) {
                          setShowCreateCustomReportModal(true);
                          return;
                        }
                        await handleSaveCustomReport({
                          forceCreate: true,
                          name,
                          description: (createCustomReportForm.description || '').trim(),
                          visibility: createCustomReportForm.visibility || 'only_me',
                        });
                      }}
                      disabled={!selectedReportId || savingCustomReport}
                    >
                      <Icon icon="mdi:content-save-outline" className="fs-5" />
                      {savingCustomReport ? 'Saving...' : 'Save Custom Report'}
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Export Button (when data is available) */}
          {reportData && reportData.length > 0 && (
            <div className="ms-auto d-flex flex-column justify-content-end">
              <div className="dropdown">
                <button
                  className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center gap-2"
                  type="button"
                  onClick={() => setShowExportMenu((prev) => !prev)}
                  disabled={exportingExcel || exportingPdf || sendNowSubmitting}
                >
                  <Icon icon="mdi:tray-arrow-down" className="icon" />
                  {exportingExcel || exportingPdf ? 'Exporting...' : 'Export'}
                </button>
                <ul className={`dropdown-menu p-12 border bg-base shadow ${showExportMenu ? 'show' : ''}`}>
                  <li>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        setShowExportMenu(false);
                        handleExportExcel();
                      }}
                      disabled={exportingExcel || exportingPdf}
                    >
                      Export to Excel
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        setShowExportMenu(false);
                        handleExportPdf({
                          orientation: resolvePdfOrientation(displayColumns.length),
                          filename: `${reportDefinition?.displayName || 'report'}_${formatFileTimestamp()}.pdf`,
                        });
                      }}
                      disabled={exportingExcel || exportingPdf}
                    >
                      Export to PDF
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Filter badges row — show selected values below the filter controls */}
        {reportDefinition && !loadingDefinition && tableFilters.length > 0 && (
          <div className="mt-8 reports-filter-badges-row">
            <Icon icon="mdi:filter-outline" className="icon text-xl line-height-1 text-secondary" />
            {renderFilterBadges()}
          </div>
        )}

        {!selectedReportId && (
          <div className="mt-16">
            {isCustomMode && (
              <div className="mb-16">
                <div className="d-flex align-items-center justify-content-between mb-8">
                  <div className="text-sm fw-semibold">My Custom Reports</div>
                </div>
                {customReports.length === 0 ? (
                  <div className="text-muted text-sm">No custom reports yet.</div>
                ) : (
                  <div className="table-responsive border rounded">
                    <table className="table bordered-table mb-0">
                      <thead>
                        <tr>
                          <th>
                            <button type="button" className="btn btn-sm btn-link p-0 text-dark d-inline-flex align-items-center gap-1" onClick={() => toggleCustomReportsSort('name')}>
                              Name <Icon icon={sortIconFor('name')} />
                            </button>
                          </th>
                          <th>
                            <button type="button" className="btn btn-sm btn-link p-0 text-dark d-inline-flex align-items-center gap-1" onClick={() => toggleCustomReportsSort('category')}>
                              Category <Icon icon={sortIconFor('category')} />
                            </button>
                          </th>
                          <th>Description</th>
                          <th>
                            <button type="button" className="btn btn-sm btn-link p-0 text-dark d-inline-flex align-items-center gap-1" onClick={() => toggleCustomReportsSort('visibility')}>
                              Visibility <Icon icon={sortIconFor('visibility')} />
                            </button>
                          </th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCustomReports.map((item) => (
                          <tr key={item.id}>
                            <td>{item.name}</td>
                            <td>{resolveCustomReportCategory(item)}</td>
                            <td className="text-muted">{item.description || '-'}</td>
                            <td>
                              <span className={`badge ${item.visibility === 'everyone' ? 'bg-success-focus text-success-main' : 'bg-primary-focus text-primary-main'}`}>
                                {item.visibility === 'everyone' ? 'Everyone' : 'Only Me'}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="view-btn w-32-px h-32-px bg-primary-focus text-primary-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                  onClick={() => applyCustomReportConfig(item)}
                                  title="View"
                                >
                                  <Icon icon="mdi:eye-outline" className="text-xl" />
                                </button>
                                <button
                                  type="button"
                                  className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                  onClick={() => {
                                    if (!isCustomReportAuthor(item)) return;
                                    applyCustomReportConfig(item);
                                    enterPreviewMode();
                                  }}
                                  title={isCustomReportAuthor(item) ? 'Edit' : 'You cannot edit this report because you are not the author.'}
                                  disabled={!isCustomReportAuthor(item) || savingCustomReport || deletingCustomReportId === item.id}
                                >
                                  <Icon icon="mdi:pencil" className="text-xl" />
                                </button>
                                <button
                                  type="button"
                                  className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                  onClick={() => {
                                    if (!isCustomReportAuthor(item)) return;
                                    setCustomReportToDelete(item);
                                  }}
                                  title={isCustomReportAuthor(item) ? 'Delete' : 'You cannot delete this report because you are not the author.'}
                                  disabled={!isCustomReportAuthor(item) || savingCustomReport || deletingCustomReportId === item.id}
                                >
                                  {deletingCustomReportId === item.id ? (
                                    <span className="spinner-border spinner-border-sm" />
                                  ) : (
                                    <Icon icon="mdi:delete-outline" className="text-xl" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                      <div className="text-muted">
                        Showing {customReportsStartRecord} to {customReportsEndRecord} of {sortedCustomReports.length} records
                      </div>
                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${customReportsPage <= 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link text-md"
                              onClick={() => setCustomReportsPage((prev) => Math.max(1, prev - 1))}
                              disabled={customReportsPage <= 1}
                            >
                              Previous
                            </button>
                          </li>
                          {Array.from({ length: Math.min(5, totalCustomReportsPages) }, (_, i) => {
                            let pageNum;
                            if (totalCustomReportsPages <= 5) {
                              pageNum = i + 1;
                            } else if (customReportsPage <= 3) {
                              pageNum = i + 1;
                            } else if (customReportsPage >= totalCustomReportsPages - 2) {
                              pageNum = totalCustomReportsPages - 4 + i;
                            } else {
                              pageNum = customReportsPage - 2 + i;
                            }
                            return (
                              <li key={pageNum} className={`page-item ${customReportsPage === pageNum ? 'active' : ''}`}>
                                <button
                                  className="page-link text-md"
                                  onClick={() => setCustomReportsPage(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          })}
                          <li className={`page-item ${customReportsPage >= totalCustomReportsPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link text-md"
                              onClick={() => setCustomReportsPage((prev) => Math.min(totalCustomReportsPages, prev + 1))}
                              disabled={customReportsPage >= totalCustomReportsPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="text-sm fw-semibold mb-12">
              {isCustomMode ? 'Create A New Custom Report From A Base Table' : 'Choose A Base Table'}
            </div>
            {isCustomMode && (
              <div className="text-muted text-sm mb-12">
                No custom report selected. Pick a saved report or choose a base table to create one.
              </div>
            )}
            <div className="row g-3">
              {reportOptions.map((option) => (
                <div
                  key={option.value}
                  className="col-6 col-sm-4 col-md-3 col-xl-2"
                  onMouseEnter={(e) => {
                    setHoveredBaseTableId(option.value);
                  }}
                  onMouseLeave={(e) => {
                    setHoveredBaseTableId(null);
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (isCustomMode) {
                        setPendingCreateBaseTableId(option.value);
                        setShowCreateCustomReportModal(true);
                        return;
                      }
                      setSelectedReportId(option.value);
                    }}
                    className="btn w-100 border bg-white d-flex flex-column align-items-center justify-content-center text-center p-12 report-base-table-tile"
                    title={isCustomMode ? `Create new report on ${option.label}` : option.label}
                  >
                    <Icon
                      icon={isCustomMode && hoveredBaseTableId === option.value ? 'mdi:plus-circle-outline' : (option.icon || 'mdi:file-chart-outline')}
                      className="mb-8 report-base-table-icon"
                    />
                    <span className="text-sm fw-medium lh-sm">{option.label}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger mt-3 mb-0">
            {error}
          </div>
        )}
      </div>

      <div className="card-body p-0 reports-body-scroll">
        {loadingDefinition && (
          <div className="d-flex align-items-center justify-content-center py-5">
            <span className="spinner-border spinner-border-sm me-2" />
            Loading report configuration...
          </div>
        )}

        {/*{!selectedReportId && !loadingDefinition && (
          <div className="d-flex align-items-center justify-content-center py-5 text-muted">
            Select a report to get started
          </div>
        )}*/}

        {selectedReportId && reportDefinition && !loadingDefinition && (
          <div className="d-flex align-items-stretch">
            {!(isCustomMode && selectedCustomReport && !isEditingCustomReportConfig) && (
            <div className="bg-white mx-16 border radius-8 reports-side-panel">
              <ul className="nav bordered-tab border border-top-0 border-start-0 border-end-0 d-inline-flex nav-pills" role="tablist">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activePanelTab === 'columns' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActivePanelTab('columns')}
                  >
                    Columns
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activePanelTab === 'filters' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActivePanelTab('filters')}
                  >
                    Filters
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activePanelTab === 'grouping' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActivePanelTab('grouping')}
                  >
                    Sort & Group
                  </button>
                </li>
              </ul>
              <div className="p-3 d-flex flex-column pb-10 reports-side-panel-content">
                {activePanelTab === 'columns' && (
                  <div className="d-flex flex-column reports-columns-panel-scroll">
                    <div className="mb-16">
                      <div className="mb-16">
                        Select the columns to be used in your report.
                      </div>
                      <div className="d-flex align-items-center gap-2 mb-12">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setSelectedFields([])}
                          disabled={selectedFields.length === 0}
                        >
                          Remove All
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setSelectedFields(fieldOptions)}
                          disabled={fieldOptions.length === 0 || selectedFields.length === fieldOptions.length}
                        >
                          Add All
                        </button>
                      </div>
                      <div className="input-group">
                        <span className="input-group-text bg-white">
                          <Icon icon="mdi:magnify" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search"
                          value={columnSearch}
                          onChange={(e) => setColumnSearch(e.target.value)} />
                      </div>
                    </div>
                    <ul className="list-group flex-grow-1 overflow-auto">
                      {filteredFieldOptions.map((option) => {
                        const isSelected = selectedFields.some((field) => field.value === option.value);
                        const showRemove = isSelected && hoveredColumnField === option.value;
                        return (
                          <li
                            key={option.value}
                            className={`list-group-item d-flex align-items-center ${isSelected ? 'text-muted' : ''} ${isSelected ? 'bg-primary-focus' : ''}`}
                            onMouseEnter={() => setHoveredColumnField(option.value)}
                            onMouseLeave={() => setHoveredColumnField(null)}
                          >
                            <span>{option.label}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link ms-auto"
                              onClick={() => (isSelected ? handleRemoveField(option.value) : handleAddField(option))}
                            >
                              <Icon
                                icon={isSelected ? (showRemove ? "mdi:close" : "mdi:check") : "mdi:plus"}
                                className={`text-lg ${isSelected ? (showRemove ? 'text-danger' : 'text-primary') : 'text-primary'}`}
                              />
                            </button>
                          </li>
                        );
                      })}
                      {filteredFieldOptions.length === 0 && (
                        <li className="list-group-item text-muted">No fields match your search.</li>
                      )}
                    </ul>
                  </div>
                )}
                {activePanelTab === 'filters' && (
                  <div className="d-flex flex-column gap-3 reports-filters-panel-scroll">
                    <div>
                      Use filters to select the information you need.
                    </div>
                    {dateRangeFilterFields && (
                      <>
                        <label className="form-label mb-2">Date Presets</label>
                        <select
                          className="form-select report-react-select-like"
                          value={selectedDatePreset}
                          onChange={(e) => handleDatePresetChange(e.target.value)}
                        >
                          <option value="">Select date preset...</option>
                          {DATE_PRESET_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                      <div className="d-flex flex-column gap-2">

                      {reportDefinition && !loadingDefinition && (
                        <>
                          {tableFilters
                            .filter((config) => {
                              if (!shouldHideAbsoluteDateFilters) return true;
                              return (
                                config.field !== dateRangeFilterFields?.fromField &&
                                config.field !== dateRangeFilterFields?.toField
                              );
                            })
                            .map((config) => renderFilterControl(config, false, 'col-md-12'))}
                        </>
                      )}
                      {(!reportDefinition.filterDefinition?.filters || reportDefinition.filterDefinition.filters.length === 0) && (
                        <div className="text-muted">No filters available.</div>
                      )}
                    </div>
                  </div>
                )}
                {activePanelTab === 'grouping' && (
                  <div className="d-flex flex-column gap-3">
                    <div>
                      Select sorting and grouping for the report.
                    </div>

                    {reportDefinition && !loadingDefinition && (
                      <div className="d-flex flex-column gap-3">
                        {/* Sort By section */}
                        <div className="d-flex flex-column gap-2 mt-3">
                          <label className="form-label mb-0 text-lg">Sort By</label>
                          {/* Add sort field dropdown */}
                          {fieldOptions.length > 0 && (
                            <div className="mb-16">
                              <Select
                                classNamePrefix="react-select"
                                options={fieldOptions.filter(
                                  (opt) => !sortByFields.some((s) => s.fieldName === opt.value)
                                )}
                                value={null}
                                onChange={(option) => {
                                  if (option) {
                                    setSortByFields((prev) => [
                                      ...prev,
                                      { fieldName: option.value, ascending: true },
                                    ]);
                                  }
                                }}
                                placeholder="Add sort field..."
                                isClearable={false}
                              />
                            </div>
                          )}

                          {/* Current sort fields */}

                          {sortByFields.map((sortField, index) => {
                            const colLabel = fieldOptions.find((f) => f.value === sortField.fieldName)?.label || sortField.fieldName;
                            return (
                              <div
                                key={index}
                                className={`d-flex align-items-center gap-2 reports-sort-row ${dragSortIndex === index ? 'reports-sort-row-dragging' : ''}`}
                                draggable
                                onDragStart={() => handleSortDragStart(index)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => handleSortDrop(index)}
                                onDragEnd={() => setDragSortIndex(null)}
                              >
                                <span
                                  className="reports-sort-drag-handle"
                                  title="Drag to reorder"
                                  aria-label="Drag to reorder"
                                >
                                  <Icon icon="mdi:drag-vertical" className="text-lg text-muted" />
                                </span>
                                <div className="d-flex flex-column flex-grow-1 gap-1">
                                  <span className="fw-semibold text-sm text-primary">{colLabel}</span>
                                  <select
                                    className="form-select form-select-sm report-react-select-like"
                                    value={sortField.ascending ? 'asc' : 'desc'}
                                    onChange={(e) => {
                                      setSortByFields((prev) => prev.map((f, i) =>
                                        i === index ? { ...f, ascending: e.target.value === 'asc' } : f
                                      ));
                                    }}
                                  >
                                    <option value="asc">Ascending</option>
                                    <option value="desc">Descending</option>
                                  </select>
                                </div>
                                <div className="d-flex flex-column gap-1">
                                  {index > 0 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0 text-muted"
                                      title="Move up"
                                      onClick={() => {
                                        setSortByFields((prev) => {
                                          const next = [...prev];
                                          [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                          return next;
                                        });
                                      }}
                                    >
                                      <Icon icon="mdi:chevron-up" className="text-primary text-xl reports-sort-move-icon" />
                                    </button>
                                  )}
                                  {index < sortByFields.length - 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0 text-muted"
                                      title="Move down"
                                      onClick={() => {
                                        setSortByFields((prev) => {
                                          const next = [...prev];
                                          [next[index], next[index + 1]] = [next[index + 1], next[index]];
                                          return next;
                                        });
                                      }}
                                    >
                                      <Icon icon="mdi:chevron-down" className="text-primary text-xl reports-sort-move-icon" />
                                    </button>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link p-0 text-muted"
                                  title="Remove"
                                  onClick={() => {
                                    setSortByFields((prev) => prev.filter((_, i) => i !== index));
                                  }}
                                >
                                  <Icon icon="mdi:close" className="text-muted text-lg reports-sort-remove-icon" />
                                </button>
                              </div>
                            );

                          })}

                        {sortByFields.length === 0 && (
                          <div className="text-muted text-sm">
                            No sort fields selected. The report will use its default ordering.
                          </div>
                        )}
                      </div>

                      <div className="reports-sort-group-divider" />

                      {/* Section By (Group By) dropdown */}
                      {sectionByOptions.length > 0 && (
                        <div className="d-flex flex-column mt-24 reports-groupby-wrap">
                            <label className="form-label text-lg">Group By</label>
                            <select
                              className="form-select report-react-select-like"
                              value={sectionBy}
                              onChange={(e) => setSectionBy(e.target.value)}
                            >
                              {sectionByOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            )}
            <div className={`flex-grow-1 ${!(isCustomMode && selectedCustomReport && !isEditingCustomReportConfig) ? 'px-3' : 'px-16'} reports-results-wrap`}>
              {selectedReportId && reportDefinition && !loadingDefinition && !reportData && !loadingResults && (
                <div className="d-flex align-items-center justify-content-center py-5 text-muted">
                  {isCustomMode
                    ? 'Set filters, columns, and grouping, then save your custom report.'
                    : 'Set your filters to view report results.'}
                </div>
              )}

              {loadingResults && (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <span className="spinner-border spinner-border-sm me-2" />
                  Running report...
                </div>
              )}

              {/* Report Results */}
              {reportData && reportDefinition && !loadingResults && (
                <ReportDataTable
                  data={reportData}
                  columns={displayColumns}
                  metadata={reportMetadata}
                  total={total}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onRemoveColumn={isViewingCustomReportMode ? undefined : handleRemoveField}
                  sortByFields={sortByFields}
                  onSortColumn={isViewingCustomReportMode ? handleResultsColumnSort : undefined}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {showSchedulesModal && !isCustomMode && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl reports-modal-xl">
              <div className="modal-content reports-modal-visible">
                <div className="modal-header">
                  <h5 className="modal-title">Scheduled Reports</h5>
                  <button className="btn-close" onClick={() => setShowSchedulesModal(false)} />
                </div>
                <div className="modal-body reports-modal-visible">
                  {filteredReportSchedules.length === 0 && (
                    <div className="text-muted">No schedules configured.</div>
                  )}
                  {filteredReportSchedules.length > 0 && (
                    <div className="table-responsive">
                      <table className="table bordered-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Frequency</th>
                            <th>Timezone</th>
                            <th>Next Run</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredReportSchedules.map((schedule) => (
                            <tr key={schedule.id}>
                              <td>{schedule.name}</td>
                              <td className="text-capitalize">{schedule.frequency}</td>
                              <td>{schedule.timezone}</td>
                              <td>{schedule.nextRunAt ? formatLocal(schedule.nextRunAt, getUserTimeZone(), 'yyyy-MM-dd HH:mm') : '-'}</td>
                              <td>
                                <span className={`badge ${((schedule.status || '').toLowerCase() === 'running' || (!schedule.status && schedule.enabled)) ? 'bg-success-focus text-success-main' : 'bg-warning-focus text-warning-main'}`}>
                                  {((schedule.status || '').toLowerCase() === 'running' || (!schedule.status && schedule.enabled)) ? 'Running' : 'Stopped'}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex gap-2 align-items-center">
                                  {canRunSchedules && (
                                    <button
                                      type="button"
                                      className="view-btn w-32-px h-32-px bg-primary-focus text-primary-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                      title="Run Now"
                                      onClick={() => handleRunScheduleNow(schedule.id)}
                                    >
                                      <Icon icon="mdi:play" className="text-xl" />
                                    </button>
                                  )}
                                  {canUpdateSchedules && Boolean(schedule.enabled) && (
                                    <button
                                      type="button"
                                      className="view-btn w-32-px h-32-px bg-warning-focus text-warning-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                      title="Stop"
                                      onClick={() => handleToggleSchedule({ ...schedule, enabled: true })}
                                    >
                                      <Icon icon="mdi:stop" className="text-xl" />
                                    </button>
                                  )}
                                  {canUpdateSchedules && !Boolean(schedule.enabled) && (
                                    <button
                                      type="button"
                                      className="view-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                      title="Start"
                                      onClick={() => handleToggleSchedule({ ...schedule, enabled: false })}
                                    >
                                      <Icon icon="mdi:play-circle-outline" className="text-xl" />
                                    </button>
                                  )}
                                  {canUpdateSchedules && (
                                    <button
                                      type="button"
                                      className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                      title="Edit"
                                      onClick={() => openEditScheduleModal(schedule)}
                                    >
                                      <Icon icon="mdi:pencil" className="text-xl" />
                                    </button>
                                  )}
                                  {canDeleteSchedules && (
                                    <button
                                      type="button"
                                      className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                      title="Delete"
                                      onClick={() => handleDeleteSchedule(schedule.id)}
                                    >
                                      <Icon icon="mdi:delete-outline" className="text-xl" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowSchedulesModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {showScheduleFormModal && !isCustomMode && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingSchedule ? 'Edit Scheduled Report' : 'Create a Scheduled Report'}</h5>
                  <button className="btn-close" onClick={() => setShowScheduleFormModal(false)} />
                </div>
                <div className="modal-body">
                  {!editingSchedule && (
                    <>
                      <p className="text-muted mb-3">
                        This will save your current report setup, including selected report, filters, columns, sort, and grouping.
                      </p>
                      <div className="mb-3 p-12 border rounded bg-light">
                        <div className="fw-semibold mb-2">Current Report Configuration</div>
                        <div className="mb-2">
                          <div className="text-sm fw-semibold mb-1">Applied Filters</div>
                          {currentAppliedFiltersForSchedulePreview.length > 0 ? (
                            <div className="d-flex flex-column gap-1">
                              {currentAppliedFiltersForSchedulePreview.map(([field, value]) => (
                                <div key={field} className="text-sm">
                                  <span className="fw-semibold">{resolveFilterLabel(field)}:</span>{' '}
                                  {formatFilterValue(value)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-muted">No filters applied.</div>
                          )}
                        </div>
                        <div className="mb-2">
                          <div className="text-sm fw-semibold mb-1">Sort &amp; Group Applied</div>
                          <div className="text-sm">
                            <span className="fw-semibold">Group By:</span>{' '}
                            {sectionBy && sectionBy !== 'none' ? resolveColumnLabel(sectionBy) : 'None'}
                          </div>
                          <div className="text-sm">
                            <span className="fw-semibold">Sort By:</span>{' '}
                            {Array.isArray(sortByFields) && sortByFields.length > 0
                              ? sortByFields
                                .map((item) => {
                                  const sortFieldName = getSortFieldName(item);
                                  const sortDirection = getSortDirectionLabel(item);
                                  return `${resolveColumnLabel(sortFieldName)} (${sortDirection})`;
                                })
                                .join(', ')
                              : 'None'}
                          </div>
                        </div>
                        {dateRangeFilterFields && selectedDatePreset && (
                          <div className="mb-2">
                            <div className="text-sm fw-semibold mb-1">Relative Date Filters</div>
                            <div className="text-sm">
                              Date Preset: {DATE_PRESET_LABEL_BY_VALUE[selectedDatePreset] || selectedDatePreset}
                            </div>
                            <div className="text-sm">
                              <code>{JSON.stringify(getRelativeDateFiltersFromPreset(selectedDatePreset))}</code>
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="text-sm fw-semibold mb-1">Included Columns</div>
                          {Array.isArray(selectedFields) && selectedFields.length > 0 ? (
                            <div className="d-flex flex-wrap gap-2">
                              {selectedFields.map((field) => (
                                <span
                                  key={field.value}
                                  className="badge bg-primary-focus text-primary-main d-inline-flex align-items-center"
                                >
                                  {resolveColumnLabel(field.value)}
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0 ms-2 text-primary"
                                    title="Remove column"
                                    onClick={() => handleRemoveField(field.value)}
                                  >
                                    <Icon icon="mdi:close" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-muted">All report default columns.</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {editingSchedule && (
                    <div className="mb-3 p-12 border rounded bg-light">
                      <div className="fw-semibold mb-2">Saved Report Configuration</div>
                      <div className="mb-2">
                        <div className="text-sm fw-semibold mb-1">Applied Filters</div>
                        {editingSchedule?.filtersJson &&
                        Object.entries(editingSchedule.filtersJson).filter(
                          ([field]) => field !== 'fromDate' && field !== 'toDate',
                        ).length > 0 ? (
                          <div className="d-flex flex-column gap-1">
                            {Object.entries(editingSchedule.filtersJson)
                              .filter(([field]) => field !== 'fromDate' && field !== 'toDate')
                              .map(([field, value]) => (
                              <div key={field} className="text-sm">
                                <span className="fw-semibold">{field}:</span>{' '}
                                {formatFilterValue(value)}
                              </div>
                              ))}
                          </div>
                        ) : (
                          <div className="text-sm text-muted">No filters saved.</div>
                        )}
                      </div>
                      {dateRangeFilterFields && (
                        <div className="mb-2">
                          <div className="text-sm fw-semibold mb-2">Relative Date Filters</div>
                          <div className="row g-2">
                            <div className="col-md-6">
                              <label className="form-label text-sm mb-1">From date mode</label>
                              <select
                                className="form-select form-select-sm report-react-select-like"
                                value={scheduleForm.relativeDateFilters?.fromDate?.mode || 'last_days'}
                                onChange={(e) =>
                                  setScheduleForm((prev) => ({
                                    ...prev,
                                    relativeDateFilters: {
                                      ...(prev.relativeDateFilters || {}),
                                      fromDate: {
                                        ...(prev.relativeDateFilters?.fromDate || {}),
                                        mode: e.target.value,
                                      },
                                    },
                                  }))
                                }
                              >
                                {FROM_DATE_MODE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-6">
                              <label className="form-label text-sm mb-1">To date mode</label>
                              <select
                                className="form-select form-select-sm report-react-select-like"
                                value={scheduleForm.relativeDateFilters?.toDate?.mode || 'today'}
                                onChange={(e) =>
                                  setScheduleForm((prev) => ({
                                    ...prev,
                                    relativeDateFilters: {
                                      ...(prev.relativeDateFilters || {}),
                                      toDate: {
                                        ...(prev.relativeDateFilters?.toDate || {}),
                                        mode: e.target.value,
                                      },
                                    },
                                  }))
                                }
                              >
                                {TO_DATE_MODE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {(scheduleForm.relativeDateFilters?.fromDate?.mode === 'last_days' ||
                              scheduleForm.relativeDateFilters?.fromDate?.mode === 'last_hours') && (
                              <>
                                <div className="col-12">
                                  <hr className="my-2" />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label text-sm mb-1">
                                    {scheduleForm.relativeDateFilters?.fromDate?.mode === 'last_hours'
                                      ? 'From date hours'
                                      : 'From date days'}
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    className="form-control form-control-sm"
                                    value={
                                      scheduleForm.relativeDateFilters?.fromDate?.mode === 'last_hours'
                                        ? scheduleForm.relativeDateFilters?.fromDate?.hours ?? 24
                                        : scheduleForm.relativeDateFilters?.fromDate?.days ?? 30
                                    }
                                    onChange={(e) =>
                                      setScheduleForm((prev) => {
                                        const mode = prev.relativeDateFilters?.fromDate?.mode;
                                        const parsed = Number(e.target.value) || 1;
                                        return {
                                          ...prev,
                                          relativeDateFilters: {
                                            ...(prev.relativeDateFilters || {}),
                                            fromDate: {
                                              ...(prev.relativeDateFilters?.fromDate || {}),
                                              ...(mode === 'last_hours'
                                                ? { hours: parsed }
                                                : { days: parsed }),
                                            },
                                          },
                                        };
                                      })
                                    }
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      <hr className="my-3" />
                      <div>
                        <div className="text-sm fw-semibold mb-2">Included Columns</div>
                        {Array.isArray(selectedFields) && selectedFields.length > 0 ? (
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            {selectedFields.map((field) => (
                              <div
                                key={field.value}
                                className="btn btn-sm btn-primary d-inline-flex align-items-center reports-chip-btn"
                              >
                                {resolveColumnLabel(field.value)}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link p-0 ms-2 text-white d-inline-flex align-items-center"
                                  title="Remove column"
                                  onClick={() => handleRemoveField(field.value)}
                                >
                                  <Icon icon="mdi:close" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-muted mb-2">All report default columns.</div>
                        )}
                        <hr className="my-3" />
                        <div className="text-sm fw-semibold mb-2">Add More Columns</div>
                        <div className="d-flex flex-wrap gap-2">
                          {fieldOptions
                            .filter(
                              (option) =>
                                !selectedFields.some((field) => field.value === option.value),
                            )
                            .map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center reports-chip-btn"
                                onClick={() => handleAddField(option)}
                              >
                                <Icon icon="mdi:plus" className="me-1" />
                                {option.label}
                              </button>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Schedule Name</label>
                      <input
                        className="form-control"
                        value={scheduleForm.name}
                        onChange={(e) => setScheduleForm((prev) => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Frequency</label>
                      <select
                        className="form-select report-react-select-like"
                        value={scheduleForm.frequency}
                        onChange={(e) => setScheduleForm((prev) => ({ ...prev, frequency: e.target.value }))}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={scheduleForm.timeOfDay}
                        onChange={(e) => setScheduleForm((prev) => ({ ...prev, timeOfDay: e.target.value }))}
                      />
                    </div>
                    {scheduleForm.frequency === 'weekly' && (
                      <div className="col-md-4">
                        <label className="form-label">Day of Week</label>
                        <select
                          className="form-select report-react-select-like"
                          value={scheduleForm.dayOfWeek}
                          onChange={(e) => setScheduleForm((prev) => ({ ...prev, dayOfWeek: Number(e.target.value) }))}
                        >
                          {WEEKDAY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {scheduleForm.frequency === 'monthly' && (
                      <div className="col-md-4">
                        <label className="form-label">Day of Month</label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          className="form-control"
                          value={scheduleForm.dayOfMonth}
                          onChange={(e) => setScheduleForm((prev) => ({ ...prev, dayOfMonth: Number(e.target.value) }))}
                        />
                      </div>
                    )}
                    <div className="col-md-4">
                      <label className="form-label">Timezone</label>
                      <Select
                        classNamePrefix="react-select"
                        options={timezoneOptions}
                        value={timezoneOptions.find((option) => option.value === scheduleForm.timezone) || null}
                        onChange={(option) =>
                          setScheduleForm((prev) => ({
                            ...prev,
                            timezone: option?.value || DEFAULT_SCHEDULE_TIMEZONE,
                          }))
                        }
                        isSearchable
                      />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Recipient Email Groups</label>
                      <Select
                        isMulti
                        classNamePrefix="react-select"
                        value={emailGroups
                          .filter((group) => scheduleForm.emailGroupIds.includes(group.id))
                          .map((group) => ({ value: group.id, label: group.groupName }))}
                        options={emailGroups.map((group) => ({ value: group.id, label: group.groupName }))}
                        onChange={(options) => {
                          setScheduleForm((prev) => ({
                            ...prev,
                            emailGroupIds: (options || []).map((option) => option.value),
                          }));
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Attachment File Formats</label>
                      <div className="d-flex flex-column gap-2">
                        {SCHEDULE_ATTACHMENT_FORMAT_OPTIONS.map((option) => {
                          const checked = (scheduleForm.attachmentFormats || []).includes(option.value);
                          return (
                            <div key={option.value} className="form-check d-flex align-items-center gap-2">
                              <input
                                className="form-check-input mt-0"
                                type="checkbox"
                                id={`schedule-attachment-${option.value}`}
                                checked={checked}
                                onChange={() => {
                                  setScheduleForm((prev) => {
                                    const current = Array.isArray(prev.attachmentFormats)
                                      ? prev.attachmentFormats
                                      : ['excel'];
                                    const next = checked
                                      ? current.filter((value) => value !== option.value)
                                      : [...current, option.value];
                                    return {
                                      ...prev,
                                      attachmentFormats: next.length ? next : ['excel'],
                                    };
                                  });
                                }}
                              />
                              <label
                                className="form-check-label mb-0"
                                htmlFor={`schedule-attachment-${option.value}`}
                              >
                                {option.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {scheduleFormError && (
                    <div className="alert alert-danger mt-3 mb-0">{scheduleFormError}</div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="report-schedule-enabled"
                        checked={Boolean(scheduleForm.enabled)}
                        onChange={(e) => setScheduleForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                      />
                      <label className="form-check-label mb-0" htmlFor="report-schedule-enabled">Enabled</label>
                    </div>
                  </div>
                  <button className="btn btn-secondary" onClick={() => setShowScheduleFormModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveSchedule}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {showSendNowModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Send Report via Email</h5>
                  <button className="btn-close" onClick={() => setShowSendNowModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="fw-semibold">Report</div>
                    <div>{reportDefinition?.displayName || 'Selected report'}</div>
                  </div>
                  <div className="mb-3">
                    <div className="fw-semibold">Current Filters</div>
                    <div className="text-muted">{buildFilterSummary() || 'No filters applied'}</div>
                  </div>
                  <div>
                    <label className="form-label">Recipient Email Groups</label>
                    <Select
                      isMulti
                      classNamePrefix="react-select"
                      value={emailGroups
                        .filter((group) => sendNowEmailGroupIds.includes(group.id))
                        .map((group) => ({ value: group.id, label: group.groupName }))}
                      options={emailGroups.map((group) => ({ value: group.id, label: group.groupName }))}
                      onChange={(options) => {
                        setSendNowEmailGroupIds((options || []).map((option) => option.value));
                      }}
                    />
                  </div>
                  <div className="mt-3">
                    <label className="form-label">Attachment Formats</label>
                    <div className="d-flex flex-column gap-2">
                      {SEND_NOW_ATTACHMENT_FORMAT_OPTIONS.map((option) => (
                        <div className="form-check" key={option.value}>
                          <input
                            id={`send-now-format-${option.value}`}
                            className="form-check-input"
                            type="checkbox"
                            checked={sendNowAttachmentFormats.includes(option.value)}
                            onChange={(e) => {
                              setSendNowAttachmentFormats((prev) => {
                                if (e.target.checked) {
                                  if (prev.includes(option.value)) return prev;
                                  return [...prev, option.value];
                                }
                                return prev.filter((format) => format !== option.value);
                              });
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`send-now-format-${option.value}`}
                          >
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {sendNowError && (
                    <div className="alert alert-danger mt-3 mb-0">{sendNowError}</div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowSendNowModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSendNowEmail} disabled={sendNowSubmitting}>
                    {sendNowSubmitting ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {showExportConfirm && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Export Report</h5>
                  <button className="btn-close" onClick={() => setShowExportConfirm(false)} />
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Exporting the full report may take some time and produce a large file
                    ({total.toLocaleString()} rows). Do you want to continue?
                  </p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowExportConfirm(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={doExportExcel}>
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {showCreateCustomReportModal && isCustomMode && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create Custom Report</h5>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowCreateCustomReportModal(false);
                      setPendingCreateBaseTableId(null);
                    }}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      className="form-control"
                      value={createCustomReportForm.name}
                      onChange={(e) => setCreateCustomReportForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      rows={3}
                      className="form-control"
                      value={createCustomReportForm.description}
                      onChange={(e) => setCreateCustomReportForm((prev) => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="form-label">Who Sees This Report</label>
                    <select
                      className="form-select report-react-select-like"
                      value={createCustomReportForm.visibility}
                      onChange={(e) => setCreateCustomReportForm((prev) => ({ ...prev, visibility: e.target.value }))}
                    >
                      <option value="only_me">Only Me</option>
                      <option value="everyone">Everyone</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateCustomReportModal(false);
                      setPendingCreateBaseTableId(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (!createCustomReportForm.name.trim()) {
                        showToastWarning('Custom report name is required.');
                        return;
                      }
                      if (pendingCreateBaseTableId) {
                        setSelectedReportId(pendingCreateBaseTableId);
                        setPendingCreateBaseTableId(null);
                      }
                      setShowCreateCustomReportModal(false);
                      setIsEditingCustomReportConfig(true);
                    }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {showEditCustomInfoModal && isCustomMode && selectedCustomReport && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Report Info</h5>
                  <button className="btn-close" onClick={() => setShowEditCustomInfoModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      className="form-control"
                      value={editCustomInfoForm.name}
                      onChange={(e) => setEditCustomInfoForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      rows={3}
                      className="form-control"
                      value={editCustomInfoForm.description}
                      onChange={(e) => setEditCustomInfoForm((prev) => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="form-label">Who Sees This Report</label>
                    <select
                      className="form-select report-react-select-like"
                      value={editCustomInfoForm.visibility}
                      onChange={(e) => setEditCustomInfoForm((prev) => ({ ...prev, visibility: e.target.value }))}
                    >
                      <option value="only_me">Only Me</option>
                      <option value="everyone">Everyone</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowEditCustomInfoModal(false)}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      await handleSaveCustomReport({
                        forceUpdateId: selectedCustomReport.id,
                        name: editCustomInfoForm.name,
                        description: editCustomInfoForm.description,
                        visibility: editCustomInfoForm.visibility,
                      });
                      setShowEditCustomInfoModal(false);
                    }}
                    disabled={savingCustomReport}
                  >
                    {savingCustomReport ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {customReportToDelete && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete Custom Report</h5>
                  <button
                    className="btn-close"
                    onClick={() => setCustomReportToDelete(null)}
                    disabled={deletingCustomReportId === customReportToDelete.id}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Are you sure you want to delete custom report
                    {' '}<strong>{customReportToDelete.name || customReportToDelete.id}</strong>?
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setCustomReportToDelete(null)}
                    disabled={deletingCustomReportId === customReportToDelete.id}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDeleteCustomReport(customReportToDelete)}
                    disabled={deletingCustomReportId === customReportToDelete.id}
                  >
                    {deletingCustomReportId === customReportToDelete.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
};

export default ReportsLayer;
